import type { BlendFile } from "../blend-reader/types/BlendFile.ts";
import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import type { ShapeKeyLayer } from "../mannequin/shapeKeyLayers.ts";
import type { BrushSelectionMode } from "../modeling/editSelection.ts";
import type { FaceBasis } from "../modeling/faceBasis.ts";
import type { LibraryEntry } from "../modeling/objectLibrary.ts";
import type { SurfaceProfileTarget } from "../modeling/surfaceProfiles.ts";
import type {
  CurveData,
  EditGranularity,
  EditModeState,
  MaterialDataBlock,
  PivotReference,
  PrimitiveKind,
  SceneLightProps,
  SceneMaterial,
  SceneObject,
  ShadingMode,
} from "../modeling/types.ts";
import type { DriverGraph } from "../scene-graph/DriverGraph.ts";
import type { DebugMaterialConfig } from "../viewport/debugMaterial.ts";
import type { TopologyVizMode } from "../viewport/topologyViz.ts";
import type { PhysicsSimProgress } from "../wasm/physicsSimWorkerClient.ts";
import type { ActiveEditSession } from "./editSession.ts";
import type { TopologyPreviewSeed } from "./storeCommandOps/topologyExecute.ts";

/**
 * Trajectory snapshot produced by `simulateGravity` when `recordStride > 0`.
 * Delivered through the `onBakeComplete` callback; the Simulate Gravity
 * menu caches it per-object and interpolates frames on Time-slider drag.
 *
 * Frames are stored in OBJECT-LOCAL space (already run through the inverse
 * world transform), so scrubbing just needs to pluck two frames, lerp,
 * and commit via `applyGravityFrame`. Frame layout: `frameCount`
 * consecutive vertex arrays, each `vertexCount * 3` Float32 values.
 */
export interface GravityBakeResult {
  objectId: string;
  frames: Float32Array;
  frameCount: number;
  vertexCount: number;
  /** True when the solver wrote zero free positions — happens if every
   *  particle was pinned. The menu treats these bakes as "nothing to
   *  scrub" and hides the Time slider effect. */
  empty: boolean;
}

/**
 * Working state for in-progress rectangle-footprint surface primitives
 * (Surface Box and Surface Cylinder). Mirrors `SurfaceBoxDraft` from
 * `modeling/surfaceBoxMath.ts`, but lives in the store so gizmos in the
 * R3F tree can subscribe alongside the handler that mutates it. The basis
 * is locked during surface selection; cornerA/cornerB and height update
 * after the footprint drag begins.
 */
export interface SurfaceFootprintDraftState {
  /** Face frame locked during surface selection. */
  basis: FaceBasis;
  /** Optional object under the silhouette; reverse extrusions may cut it. */
  hostObjectId: string | null;
  /** First rectangle corner in world space (lies on the basis plane). */
  cornerA: [number, number, number];
  /** Second rectangle corner in world space (lies on the basis plane). */
  cornerB: [number, number, number];
  /** Signed extrusion distance along `basis.normal` (positive = outward). */
  height: number;
  /** Face by default; edge targets turn the height stage into a ramp profile. */
  profileTarget?: SurfaceProfileTarget;
}

export type SurfaceBoxDraftState = SurfaceFootprintDraftState;
export type SurfaceCylinderDraftState = SurfaceFootprintDraftState;

/**
 * Live drag preview during Surface Poly's drawing stage. Read by
 * `SurfacePolyGizmo` to color the rubber-band segment (red on
 * self-intersection, green on imminent closure, default otherwise).
 */
export interface SurfacePolyPreview {
  /** Which endpoint the drag started at — drives where the new vertex appends. */
  fromEnd: "head" | "tail";
  /** World-space tip after snap projection + 45° clamp. */
  candidate: [number, number, number];
  /** True → the segment crosses an existing edge; release will be rejected. */
  selfIntersects: boolean;
  /** True → release will close the loop (cursor within closure threshold of opposite endpoint). */
  willClose: boolean;
}

/**
 * Working state for an in-progress Surface Poly interaction. Mirrors the
 * Surface Box draft pattern; lives in the store so the gizmo can subscribe.
 * The handler keeps the per-segment undo / redo stacks in local refs
 * (high-frequency mutation; the gizmo only needs the derived `vertices` /
 * `closed` snapshot).
 */
export interface SurfacePolyDraftState {
  /** Face frame locked during surface selection. */
  basis: FaceBasis;
  /** Optional object under the silhouette; reverse extrusion may cut it. */
  hostObjectId: string | null;
  /**
   * Polyline vertices in order. Index 0 = "head" endpoint; last entry =
   * "tail" endpoint. Both endpoints are valid drag-start anchors.
   */
  vertices: ReadonlyArray<[number, number, number]>;
  /** True once the loop has closed; the extrude stage is active. */
  closed: boolean;
  /** Live drag preview — null when no drag is in progress. */
  preview: SurfacePolyPreview | null;
  /** Signed extrusion distance along `basis.normal`; meaningful in extrude stage. */
  height: number;
  /** Face by default; edge targets turn the extrude stage into a ramp profile. */
  profileTarget?: SurfaceProfileTarget;
}

/**
 * Working state for an in-progress Surface Inset interaction. The host face's
 * boundary is captured + simplified at face-pick time and is centered on the
 * cap centroid (so the resulting object's local origin lines up with the
 * picked-region centroid). Subsequent stages drive `insetDistance` and
 * `height` via the inset ring + extrude arrow gizmos.
 */
export interface SurfaceInsetDraftState {
  /** Face frame locked at surface selection. */
  basis: FaceBasis;
  /** Host object id; reverse extrusions cut into it via boolean modifier. */
  hostObjectId: string | null;
  /**
   * Picked-face boundary in basis-UV, simplified (collinear runs collapsed)
   * and re-centered on the cap centroid. This is the "outer" loop the inset
   * shrinks inward from.
   */
  boundaryLoop: ReadonlyArray<readonly [number, number]>;
  /** Cap centroid in world space — geometry's local origin after confirm. */
  centroid: [number, number, number];
  /** Inset offset distance in world units (always ≥ 0). */
  insetDistance: number;
  /** Signed extrusion distance along `basis.normal` (positive = outward). */
  height: number;
  /** Face by default; edge targets turn the extrude stage into a ramp profile. */
  profileTarget?: SurfaceProfileTarget;
}

// ---------------------------------------------------------------------------
// Surface tool — polymorphic single-slot state
// ---------------------------------------------------------------------------

/** Identifier for each Object Mode surface tool. */
export type SurfaceToolKind = "box" | "cylinder" | "poly" | "inset";

/** Modal substate for the rectangle-footprint tools (Box, Cylinder). */
export type SurfaceFootprintStage = "select" | "footprint" | "height";
/** Modal substate for Surface Poly. */
export type SurfacePolyStage = "select" | "drawing" | "extrude";
/** Modal substate for Surface Inset (single post-pick stage). */
export type SurfaceInsetStage = "edit";

/**
 * Polymorphic state for the four Object Mode surface tools. Discriminated
 * by `kind`; narrowing exposes the right `stage` and `draft` types.
 *
 * Box and Cylinder share the rectangle-footprint draft + stage shape. Poly
 * has its own polyline-draft stages. Inset has a single edit stage.
 */
export type SurfaceToolState =
  | {
      kind: "box";
      stage: SurfaceFootprintStage | null;
      draft: SurfaceFootprintDraftState | null;
      active: boolean;
    }
  | {
      kind: "cylinder";
      stage: SurfaceFootprintStage | null;
      draft: SurfaceFootprintDraftState | null;
      active: boolean;
    }
  | {
      kind: "poly";
      stage: SurfacePolyStage | null;
      draft: SurfacePolyDraftState | null;
      active: boolean;
    }
  | {
      kind: "inset";
      stage: SurfaceInsetStage | null;
      draft: SurfaceInsetDraftState | null;
      active: boolean;
    };

/** Identifier for each debug panel (used for open/closed persistence). */
export type DebugPanelId =
  | "debugOverlay"
  | "debugMaterial"
  | "bevelDebug"
  | "boneInfo"
  | "jointAngles"
  | "shapeKeyLayers"
  | "correctiveShapeKeys"
  | "poseCapture"
  | "topologyViz"
  | "shadingDebug"
  | "console"
  | "recorder"
  | "memoryProbe";

/** Primitive symmetry debug axis. Null means disabled. */
export type PrimitiveSymmetryDebugAxis = "x" | "y" | "z";

/** Shading debug flags for diagnosing normal issues on morphed mannequins.
 *  All default to false (off). Only active in debug mode + pose mode.
 *
 *  Note: normals are CPU-recomputed from the fully morphed+skinned geometry
 *  by default for all mannequin materials (see MannequinRenderer.tsx). These
 *  flags provide additional diagnostic tools that layer on top of that. */
export interface ShadingDebugFlags {
  /** Shader patch: override the fragment normal with screen-space derivatives
   *  (dFdx/dFdy of view position). Produces geometry-accurate normals from
   *  the actual morphed+skinned surface — equivalent to flat shading but via
   *  shader patching so it can be combined with other patches. */
  screenSpaceNormals: boolean;
  /** Shader patch: blend vertex-interpolated normals with screen-space flat
   *  normals at 50/50. Gives geometry-correct normal direction with reduced
   *  faceting compared to pure flat/screen-space normals. */
  blendedNormals: boolean;
  /** Strip the normal map so shading uses only vertex normals */
  disableNormalMap: boolean;
  /** Override to flat shading (per-face normals) */
  flatShading: boolean;
  /** Color fragments by world-space normal direction: R=X, G=Y, B=Z (mapped [-1,1] -> [0,1]).
   *  Screenshot-friendly — exact normal values readable from pixel colors. */
  normalDirectionViz: boolean;
  /** Show raw GPU compute normal buffer as vertex colors.
   *  Isolates "compute is wrong" from "material/transform is wrong". */
  gpuNormalBufferViz: boolean;
  /** Show active morph target weights as a per-vertex heatmap.
   *  Diagnoses corrective shape key and morph blending issues. */
  morphInfluenceHeatmap: boolean;
  /** Render the normal map texture as surface color (unlit).
   *  Shows exactly what the baked normal map contributes to the surface. */
  normalMapViz: boolean;
  /** GPU skinning: blend linear (LBS) with dual quaternion (DQS) skinning.
   *  DQS preserves volume at bent/twisted joints, reducing candy-wrapper and
   *  volume loss artifacts. Trial mode — does not affect non-debug renders. */
  dqsSkinning: boolean;
  /** Post-skinning Laplacian delta smooth (corrective smooth).
   *  Smooths the deformation delta to reduce LBS artifacts while preserving
   *  the overall silhouette. Trial mode — does not affect non-debug renders. */
  correctiveSmooth: boolean;
  /** Show per-vertex bone influence weights as a heatmap when a bone is
   *  selected in pose mode (red = 1.0, blue = 0.0). */
  boneInfluenceViz: boolean;
  /** Color each UV island a distinct random color (unlit).
   *  Pinpoints exactly where UV seam boundaries fall on the mesh surface. */
  uvIslandViz: boolean;
}

export type ViewMode = "matcap" | "material" | "wireframe";

/** High-level workspace mode — sits above InteractionMode/transformMode/viewMode. */
export type WorkspaceMode =
  | "object"
  | "reference"
  | "pose"
  | "edit"
  | "sculpt"
  | "sketch"
  | "render"
  | "inking";

export interface StoreActionFeedbackOptions {
  toast?: boolean;
}

import type { ProportionalFalloff } from "../modeling/proportional.ts";

export type { ProportionalFalloff } from "../modeling/proportional.ts";

export type SnapTarget =
  | "grid"
  | "vertex"
  | "edge"
  | "face"
  | "surface"
  | "object";

/**
 * Targets for `snapCursorTo`. Mirrors the entries in Blender's Shift+S
 * "Snap Cursor To" menu. Every entry resolves to a single world-space
 * point — the cursor jumps there and any downstream pivot listeners
 * react automatically because `pivotReference === "cursor"` reads the
 * cursor position on demand.
 */
export type SnapCursorToKind =
  | "worldOrigin" // [0, 0, 0]
  | "grid" // round to nearest unit on the XZ grid
  | "selection" // median of the current mode's selection
  | "selectionActive" // first selected element's position
  | "selectionFace" // median of currently-selected faces (edit face mode)
  | "activeBone"; // active bone's world head position (pose mode)

/**
 * Targets for `snapSelectionToCursor`. These move the current selection
 * onto the cursor. `offset` preserves relative spacing so a whole cluster
 * can be re-anchored without losing its shape.
 */
export type SnapSelectionToCursorKind =
  | "selection" // objects: set each selected object's position to the cursor
  // edit mode: median of selection lands on the cursor
  | "offset" // translate the selection so its median hits the cursor
  // while preserving the relative offset of every element
  | "origin"; // object mode only: move each selected object's origin
// (not its geometry) onto the cursor

/** Camera projection mode. */
export type CameraProjection = "perspective" | "orthographic";

/** World axis for axis-aligned camera views. */
export type ViewAxis = "x" | "y" | "z";

/** Sign along a view axis: +1 or −1 (e.g. `y` with `-1` is the Front view). */
export type ViewSign = 1 | -1;

/**
 * Transient state describing an axis-aligned orthographic view lock.
 * Null when the camera is freely user-controlled.
 *
 * `prev*` fields are snapshotted at lock entry so the user's previous state
 * can be restored when the lock breaks — either by rotating beyond the
 * break-out threshold or by explicitly dismissing. `prevOrthographicHalfHeight`
 * is null when entering from perspective (there is no prior ortho scale to
 * restore); when re-entering a view from a prior axis-lock we snapshot the
 * scale too so persp→ortho→persp round-trips match apparent framing.
 */
export interface ViewLock {
  axis: ViewAxis;
  sign: ViewSign;
  prevProjection: CameraProjection;
  prevFocalLength: number;
  prevOrthographicHalfHeight: number | null;
}

/** What to include in the rendered/exported image. */
export type RenderExportMode = "full" | "inking";

/**
 * Global override for per-material pattern overlays in Render mode.
 * - `off`: force-hide patterns regardless of each material's `gridPattern`.
 * - `defer`: respect each material's `gridPattern` setting (default).
 * - `on`: force-show a uniform grid on materials that have no `gridPattern`
 *   set, reusing that material's cell size / color / opacity (or defaults).
 *   Materials that already have an explicit pattern keep it unchanged.
 *
 * Only applies when the workspace is in Render mode; other modes always
 * respect the per-material setting.
 */
export type RenderGridOverride = "off" | "defer" | "on";

/** Interaction mode — "edit" is vertex-level editing of a single scene object */
export type InteractionMode = "navigate" | "transform" | "edit";

// ---------------------------------------------------------------------------
// Eyedropper session — transient state while the user picks a scene object
// to populate a Select / SelectSearch field.
// ---------------------------------------------------------------------------

/**
 * Set by `beginEyedropperSession` when a Select/SelectSearch field arms the
 * viewport pick mode. Cleared by `endEyedropperSession` after a successful
 * pick or cancellation.
 */
export interface EyedropperSession {
  /** Called with the tapped object's id when the user clicks a scene object. */
  onPick: (objectId: string) => void;
  /** Called when the pick is cancelled (Escape, background click, unmount). */
  onCancel: () => void;
  /**
   * Optional object-id predicate used to reject invalid picks in the viewport
   * itself. When present, disallowed objects neither highlight nor end the
   * session when clicked.
   */
  canPick?: (objectId: string) => boolean;
}

// ---------------------------------------------------------------------------
// Collection — Blender-style grouping of objects
// ---------------------------------------------------------------------------

export interface Collection {
  id: string;
  name: string;
  /** Parent transform applied to every member object. */
  transform: GroupTransform;
  objectIds: Set<string>;
  /**
   * Optional cutter-group metadata — present when the collection contains
   * hidden boolean cutters for a host object. `objectIds` remains the full
   * collection membership; this ref identifies the host and the subset of
   * members that are cutters so UI can offer targeted bake controls.
   */
  cutterRef?: CollectionCutterRef;
  /**
   * Optional generator reference — present when this collection was
   * produced by a compound object generator (e.g. Room). The collection
   * is the authoritative owner of the generator parameters; its members
   * are the generated children. Selecting the collection surfaces the
   * generator panel in the inspector.
   *
   * Cleared ("baked") on the first destructive edit-mode op against any
   * member. See `storeEditOps.cascadeGeneratorBake`.
   */
  generatorRef?: import("../modeling/generators/types.ts").CollectionGeneratorRef;
}

export interface CollectionCutterRef {
  /** SceneObject receiving boolean modifiers from the cutters. */
  hostObjectId: string;
  /** SceneObject ids used as boolean cutters for the host. */
  cutterObjectIds: Set<string>;
}

// ---------------------------------------------------------------------------
// Modifier override — runtime per-modifier state separate from BlendFile
// ---------------------------------------------------------------------------

/** Per-modifier runtime override. Stores enable state, property edits, and runtime-added modifiers. */
export interface ModifierOverride {
  enabled?: boolean;
  /** Sparse property overrides — keys match BlendModifier fields */
  props?: Record<string, unknown>;
  /** Full modifier for modifiers added at runtime (not from .blend file) */
  runtimeModifier?: BlendModifier;
  /** True when the modifier was explicitly added by the user (not from .blend file). */
  userAdded?: boolean;
  /**
   * True when this override was installed by a generator (via the
   * `generatorModifiers` field on its build output) or by a generator-adjacent
   * object-mode operation. Generator-owned overrides form contiguous blocks
   * that the store replaces as units. User-added overrides around the block
   * are preserved across rebuilds so modifiers stacked on top of a cable's
   * auto-Skin keep working.
   */
  generatorOwned?: boolean;
  /**
   * Distinguishes independently-managed generator-owned blocks on the same
   * object. Missing means legacy unscoped ownership; replacement helpers infer
   * the intended block from context for pre-field scenes/undo snapshots.
   */
  generatorOwner?: "generatorOutput" | "objectCreateArray";
}

// ---------------------------------------------------------------------------
// Command buffer — Blender-style G/R/S numeric input
// ---------------------------------------------------------------------------

export type CommandType =
  | "translate"
  | "rotate"
  | "scale"
  | "extrude"
  | "extrudeAlongNormals"
  | "extrudeIndividual"
  | "extrudeManifold"
  | "subdivide"
  | "inset"
  | "bevel"
  | "bridgeEdgeLoops"
  | "gridFill"
  | "mergeByDistance"
  | "edgeSlide"
  | "edgeLoopInsert"
  | "shrinkFatten"
  | "skinRadius"
  | "boneRotate"
  | "boneTranslate"
  | "cursorTranslate"
  | "selectSimilarNormal"
  | "surfaceBox"
  | "surfaceCylinder"
  | "surfacePoly";
export type AxisConstraint = "x" | "y" | "z" | "normal" | null;
export type TransformOrientation = "global" | "local" | "normal" | "view";

/**
 * Per-vertex rail for edge slide.
 * The vertex slides along its connected edges: toward neighbor A (t < 0) or
 * toward neighbor B (t > 0). At t = 0 the vertex is at its original position.
 */
export interface EdgeSlideRail {
  /** Vertex's original position (t = 0). */
  origin: [number, number, number];
  /** Neighbor A position (reached at t = -1). */
  a: [number, number, number];
  /** Neighbor B position (reached at t = +1), or null if only one neighbor. */
  b: [number, number, number] | null;
}

export interface CommandBuffer {
  type: CommandType;
  axis: AxisConstraint;
  /** Raw numeric string being typed, e.g. "-1.5" */
  numericInput: string;
  /** Screen position where the command was initiated (for mouse-driven transforms) */
  mouseOrigin: [number, number] | null;
  /** Object-local normal vector for "normal" axis constraint */
  normalVector: [number, number, number] | null;
  /** Bone-local axis for pose-mode rotation (preserves euler axis independence) */
  boneLocalAxis: "x" | "y" | "z" | null;
  /** Pre-operation vertex selection, restored on cancel (e.g. undo extrude+translate) */
  preEditSelection: Set<number> | null;
  /** Pre-operation edge selection, used by edge-based topology ops like bevel. */
  preEditEdges: Set<number> | null;
  /** Pre-operation face selection — authoritative source for face-mode operations
   *  like inset/bevel that run each frame from the snapshot during drag. */
  preEditFaces: Set<number> | null;
  /** Active element snapshot for deterministic topology replay and seed resolution. */
  preEditActiveVertex: number | null;
  preEditActiveEdge: number | null;
  preEditActiveFace: number | null;
  /** Pre-computed per-vertex slide rails (only for edgeSlide commands). */
  edgeSlideRails: Map<number, EdgeSlideRail> | null;
  /** True when this command was initiated by a gizmo drag (gizmo owns confirm/cancel). */
  gizmoInitiated: boolean;
  /** Seed edge for edge loop insert (only for edgeLoopInsert commands). */
  edgeLoopInsertEdge: [number, number] | null;
  /** Edge currently hovered during pick-edge mode (for reactive preview). */
  edgeLoopHoverEdge: [number, number] | null;
  /** Preview positions for edge loop insert (line-segment pairs). */
  edgeLoopPreview: Float32Array | null;
  /** Captured bone data for boneRotate/boneTranslate (set at command entry). */
  boneCommandData: {
    /** Bone's local quaternion at command start [x,y,z,w] */
    origLocalQuat: [number, number, number, number];
    /** Parent bone's world quaternion at command start [x,y,z,w] */
    parentWorldQuat: [number, number, number, number];
    /** IK chain state captured at command start (boneTranslate only) */
    ikChainState?: unknown;
    /** Original positions before IK solve (boneTranslate only) */
    ikOrigPositions?: unknown;
    /** Pole target direction for IK (boneTranslate only) */
    ikPoleDirection?: [number, number, number];
    /** IK solver type for this chain */
    ikSolverType?: "analytical" | "jacobian";
    /** Per-joint mobility weights for IK solver (0 = locked, 1 = free) */
    ikJointWeights?: number[];
    /** Body-grab mode: auto-pinned extremity positions captured at drag start.
     *  When present, these are merged with user pins so the whole-body IK solver
     *  keeps hands/feet planted while the hips move toward the drag target. */
    bodyGrabPins?: Map<string, [number, number, number]>;
  } | null;
  /** Pre-computed proportional editing weights: unselected vertex → falloff weight [0,1]. */
  proportionalWeights: Map<number, number> | null;
  /** Edit-mode pivot captured at command-enter from the snapshot geometry.
   *  Reused across pointermove frames so live preview doesn't rebuild
   *  selection-position tuple arrays on every drag tick. */
  editPivot: [number, number, number] | null;
  /** Snapshot-ordered selected vertex list for edit-mode transforms.
   *  Cached once at command-enter so rotate/scale paths don't materialize
   *  a fresh `Array.from(selectedVertices)` every pointermove frame. */
  editSelectionVertices: number[] | null;
  /** Identity of `em.selectedVertices` captured at command-enter. Lets
   *  live-apply assert in dev that the selection hasn't been swapped
   *  underneath the cached pivot/component/proportional-pivot data —
   *  those caches are only valid for the selection they were built from.
   *  Null when no edit-mode caches were populated. */
  preEditSelectionIdentity: Set<number> | null;
  /** Operator-specific parameters (keyed by ParamDef.key from operatorParams registry).
   *  Empty record {} for unparameterized commands (translate/rotate/scale). */
  params: Record<string, number | boolean | string>;
  /** Which param key the mouse drives during modal. Null = use primaryInput from schema. */
  activeValueMode: string | null;
  /** Pre-computed per-component pivots when individual origins is enabled.
   *  Component membership and pivots are command-stable — selection and
   *  topology don't change mid-drag, and pivots come from the snapshot
   *  vertices — so we build them once at command-enter rather than
   *  rebuilding via DFS over edge/face adjacency every pointermove frame.
   *  Null when individual origins is disabled (single shared pivot path). */
  individualOriginsComponents:
    | import("./storeCommandOps/transformMath.ts").EditTransformComponent[]
    | null;
  /** Pre-computed nearest-pivot lookup for proportional-edit vertices when
   *  individual origins is enabled. Avoids the per-frame O(components)
   *  nearest-pivot search inside `applyEditComponentTransform`. Null when
   *  either proportional editing or individual origins is disabled. */
  individualOriginsProportionalPivots: Map<
    number,
    [number, number, number]
  > | null;
  /** Pre-resolved set of generator-backed owners that depend on the
   *  objects being transformed. Captured at command-enter; the live
   *  drag path schedules these directly each frame instead of
   *  re-scanning all generators per moved target. Null for non-object-
   *  mode commands or when the snapshot has no scene-side dependencies
   *  to resolve. */
  dependentRebuildTargets:
    | import("./storeGeneratorOps.ts").GeneratorTarget[]
    | null;
  /** Stable preview seed for topology commands that can reuse one
   *  topological expansion during drag instead of recomputing it each frame. */
  topologyPreviewSeed: TopologyPreviewSeed | null;
  /** Monotonic timestamp of the last heavy topology preview application.
   *  Replay-based topology operators coalesce pointer-rate updates and only
   *  rebuild preview geometry at their strategy cadence. */
  topologyPreviewLastAppliedAt: number;
}

// ---------------------------------------------------------------------------
// Last operation — redo/adjust panel state
// ---------------------------------------------------------------------------

/**
 * Tag identifying which replay strategy interprets a `LastOperation`'s
 * `capture` payload. Defined on the metadata record (see
 * `COMMAND_METADATA[type].replayKind`) and copied onto each captured op so
 * `adjustLastOperation` can dispatch without re-reading metadata.
 *
 * - `geometry-snapshot`: the op mutated topology; `capture` holds the
 *   pre-op geometry snapshot plus the selection/active-element state needed
 *   to re-seed `executeTopologyOp`. Used by extrude/bevel/inset/etc.
 * - `selection-seed`: the op only mutated selection; `capture` holds the
 *   pre-op face seed plus the hidden-face mask frozen at confirm time, so
 *   the slider replays deterministically against the original seed.
 * - `object-create`: the op created an object and can replay object-level
 *   params without a topology snapshot. Surface creation tools do not use
 *   this; their controls live on persistent generators.
 */
export type ReplayKind =
  | "geometry-snapshot"
  | "selection-seed"
  | "object-create";

/**
 * Pre-op state needed to replay a topology-mutating op against a fresh
 * parameter set: the geometry snapshot plus the selection / active-element
 * context that `executeTopologyOp` consumes.
 */
export interface GeometrySnapshotCapture {
  kind: "geometry-snapshot";
  snapshot: CommandSnapshot;
  objectId: string | null;
  preEditFaces: Set<number> | null;
  preEditSelection: Set<number> | null;
  preEditEdges: Set<number> | null;
  preEditActiveVertex: number | null;
  preEditActiveEdge: number | null;
  preEditActiveFace: number | null;
  edgeLoopInsertEdge: [number, number] | null;
}

/**
 * Pre-op state needed to replay a selection-only op: the seed face set and
 * the hidden-face mask at confirm time. Frozen so the redo-panel slider
 * always recomputes from the same baseline regardless of intervening user
 * selection changes (mirrors how geometry-snapshot replay ignores live
 * geometry edits between confirm and adjust).
 */
export interface SelectionSeedCapture {
  kind: "selection-seed";
  objectId: string;
  seedFaces: ReadonlySet<number>;
  hiddenFacesAtSeedTime: ReadonlySet<number>;
}

/**
 * State needed to replay an object-creation operator through the redo panel.
 * Surface creation tools are persistent generators and intentionally do not
 * use this path; the shape stays generic for future object-mode operators.
 */
export interface ObjectCreateCapture {
  kind: "object-create";
  objectId: string;
}

export type LastOperationCapture =
  | GeometrySnapshotCapture
  | SelectionSeedCapture
  | ObjectCreateCapture;

/** Stored result of the last confirmed parameterized operation.
 *  The redo panel reads this to allow post-confirm parameter adjustment.
 *  The capture is opaque from this module's perspective — the registered
 *  replay strategy in `replayStrategies.ts` narrows it. */
export interface LastOperation {
  type: CommandType;
  /** Frozen parameter values at time of confirm. */
  params: Record<string, number | boolean | string>;
  /** Replay strategy that produced (and will consume) `capture`. */
  replayKind: ReplayKind;
  /** Strategy-specific pre-op state needed to replay the op. */
  capture: LastOperationCapture;
}

// ---------------------------------------------------------------------------
// Pose mode — bone posing for mannequins
// ---------------------------------------------------------------------------

/** Pose mode state — active when posing a mannequin's skeleton. */
export interface PoseModeState {
  /** ID of the mannequin being posed */
  objectId: string;
  /** All selected bones */
  selectedBones: Set<string>;
  /** The active bone — last clicked, used for gizmo positioning. Null if nothing selected. */
  activeBone: string | null;
}

// ---------------------------------------------------------------------------
// Sculpt mode — pressure-sensitive vertex displacement
// ---------------------------------------------------------------------------

/** Brush tools available in sculpt mode. */
export type SculptBrushType = "draw" | "smooth" | "flatten" | "grab";

/** Falloff curve presets controlling how brush intensity attenuates with distance. */
export type SculptFalloffCurve = "smooth" | "sharp" | "constant" | "linear";

/** Sculpt mode state — active when sculpting a scene object's vertices. */
export interface SculptModeState {
  /** ID of the scene object being sculpted */
  objectId: string;
  /** Active brush tool */
  brush: SculptBrushType;
  /** Brush radius in screen-space CSS pixels (view-independent sizing) */
  radius: number;
  /** Brush strength [0–1] — multiplied by pen pressure */
  strength: number;
  /** Whether the brush subtracts (inverts displacement direction) */
  invert: boolean;
  /** Spacing between dabs as a fraction of brush radius [0.01–1] */
  spacing: number;
  /** Falloff curve preset for brush attenuation. */
  falloffCurve: SculptFalloffCurve;
  /** Lazy mouse stabilizer radius in screen-space CSS pixels. 0 = disabled. */
  lazyRadius: number;
  /** Implicit smooth factor applied after each non-smooth brush [0–1]. */
  autoSmooth: number;
  /** Whether to skip vertices facing away from the camera. */
  backfaceCull: boolean;
}

// ---------------------------------------------------------------------------
// Sketch mode - stroke-based surface drawing
// ---------------------------------------------------------------------------

/** Tools available in sketch mode. */
export type SketchToolType = "pencil" | "erase";

/** Pressure-response modes shared by drawing tools. */
export type DrawingPressureMode = "size" | "opacity";

/**
 * Projection mode for sketch strokes.
 *  - "projected"   — strokes lock to the object surface under the cursor
 *                    (current default; deforms with the host's skeleton).
 *  - "screenspace" — strokes are placed 1 unit from the camera at draw time
 *                    and baked into world space. After pointerup they float
 *                    in space; orbiting the camera does NOT move them.
 */
export type SketchProjectionMode = "projected" | "screenspace";

/** Point payload accepted by the hot in-flight sketch stroke mutators. */
export interface SketchStrokePointInput {
  attachment?: {
    objectId: string;
    topologyVersion: number;
  };
  p: [number, number, number];
  pressure: number;
  skinIndex?: [number, number, number, number];
  skinWeight?: [number, number, number, number];
  corners?: [number, number, number];
  bary?: [number, number, number];
}

/**
 * Sketch mode state - active when the workspace is "sketch".
 *
 * Sketch mode is not bound to a specific scene object. The user can
 * enter sketch mode with no selection; projected stroke points attach to
 * whichever object surface the cursor hits, while screenspace strokes float
 * 1 unit from the camera ("screenspace"). Layers and strokes live at
 * `AppState.sketch`, not on any individual SceneObject.
 */
export interface SketchModeState {
  /** Active sketch tool */
  tool: SketchToolType;
  /** Stroke thickness in screen-space CSS pixels (view-independent sizing) */
  thickness: number;
  /** Stroke color as hex string */
  color: string;
  /** ID of the active stroke layer being drawn into. May be empty or stale
   *  after a layer is deleted; `beginStroke` falls back to the last layer. */
  activeLayerId: string;
  /** Projection mode for new strokes — see `SketchProjectionMode`. */
  projection: SketchProjectionMode;
}

// ---------------------------------------------------------------------------
// Inking mode — CPU-authoritative stroke pipeline
// ---------------------------------------------------------------------------

/** Which feature detectors are active for inking stroke generation. */
export interface InkingFeatureFlags {
  silhouette: boolean;
  crease: boolean;
  screenEdges: boolean;
}

/** Phase-label shown next to the progress indicator while a bake is running. */
export type InkingBakePhase = "init" | "features" | "strokes";

/** Inking canvas sub-mode: navigation / preview (`view`) vs drawable (`edit`). */
export type InkingEditSubMode = "view" | "edit";

/** Brush tools available in inking edit sub-mode. */
export type InkingTool = "pen" | "eraser";

/** Inking mode state — active when the user enters the inking workspace. */
export interface InkingModeState {
  /** Whether the CPU pipeline has produced strokes for the current view. */
  baked: boolean;
  /** Whether a bake is currently in progress. */
  baking: boolean;
  /** 0–1 bake progress, updated by the inking worker at each phase boundary. */
  bakeProgress: number;
  /** Current pipeline phase (for the progress panel label). */
  bakePhase: InkingBakePhase;
  /** Feature extraction toggles. */
  features: InkingFeatureFlags;
  /** Crease angle threshold in degrees [0–180]. */
  creaseAngle: number;
  /** Taper strength [0–1] — controls endpoint tapering of strokes. */
  taperStrength: number;
  /** Polyline simplification tolerance in pixels. */
  simplificationTolerance: number;
  /** Corner detection angle threshold in degrees. */
  cornerAngle: number;

  // --- Editable canvas sub-mode ---
  /** Canvas sub-mode: "view" (read-only preview) or "edit" (drawable). */
  editSubMode: InkingEditSubMode;
  /** Active edit tool (pen/eraser). */
  tool: InkingTool;
  /** Brush radius in page CSS pixels [1–200]. */
  size: number;
  /** Brush opacity [0–1]. */
  opacity: number;
  /** Edge falloff [0–1]: 1 = hard, 0 = feathered. */
  hardness: number;
  /** Pen color (hex). */
  color: string;
  /** What pen pressure modulates. */
  pressureMode: DrawingPressureMode;
  /** When true the faded 3D render stays ghost-visible behind the page. */
  showReference: boolean;
  /** Page pan in viewport CSS pixels. */
  pan: [number, number];
  /** Page zoom factor (1 = fit). */
  zoom: number;
  /** Page rotation in degrees (0 = upright). */
  rotation: number;
  /** Editable raster pixels (RGBA premultiplied by opacity); null until baked. */
  rasterBuffer: Uint8ClampedArray | null;
  /** Raster pixel width (matches renderWidth at bake time). */
  rasterWidth: number;
  /** Raster pixel height (matches renderHeight at bake time). */
  rasterHeight: number;
  /** Monotonic counter bumped when the raster changes, to trigger redraws. */
  rasterSeq: number;
  /** True after bake; flips to false after the first user edit. */
  rasterDirtyFromBake: boolean;
}

/**
 * Saved inking — a full-resolution raster snapshot paired with the camera
 * state that produced it. Listed in the Inking-mode sidebar; tapping one
 * restores the camera view and reloads the raster into the editable canvas
 * so the user can pick up where they left off (or finalise an export).
 *
 * The raster is stored as a PNG data URL: it compresses well (line art is
 * dominated by large transparent/uniform regions) and decodes back to a
 * Uint8ClampedArray on demand. The thumbnail is a downscaled PNG used only
 * by the sidebar list, keeping render cheap.
 */
export interface SavedInkingView {
  id: string;
  name: string;
  createdAt: number;
  /** PNG data URL of the full raster (matches rasterWidth × rasterHeight). */
  rasterPng: string;
  rasterWidth: number;
  rasterHeight: number;
  /** PNG data URL of a ~160 px wide thumbnail for the sidebar list. */
  thumbnail: string;
  /** Camera state at save time. */
  cameraPosition: [number, number, number];
  cameraTarget: [number, number, number];
  focalLength: number;
  /** ID of the camera bookmark auto-created alongside this save, if any. */
  cameraBookmarkId?: string;
}

// ---------------------------------------------------------------------------
// Reference image layer
// ---------------------------------------------------------------------------

/** A single reference image layer with independent transform and visibility. */
export interface ReferenceLayer {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** SHA-256 content hash for local IndexedDB lookup (or legacy R2 key for migration) */
  imageKey: string;
  /** Resolved blob URL for display (from local IndexedDB) */
  imageUrl: string;
  /** Pixel offset from viewport center [x, y] */
  offset: [number, number];
  /** Scale factor (0.05–10, default 1) */
  scale: number;
  /** Rotation in degrees */
  rotation: number;
  /** Opacity (0–1, default 0.3) */
  opacity: number;
  /** Whether this layer renders above (true) or below (false) the 3D scene */
  above: boolean;
  /** Visibility toggle */
  visible: boolean;
  /** Vanishing lines drawn for camera alignment */
  vanishingLines?: VanishingLine[];
  /** Ground plane Z position for camera solver */
  groundPlaneZ?: number;
  /** When true, camera updates in real-time as vanishing lines change */
  liveSolve?: boolean;
  /** Known ground-plane distance for camera distance recovery */
  referenceDistance?: ReferenceDistance;
}

/** Two image-space handles specifying a known world-space distance on the ground plane. */
export interface ReferenceDistance {
  /** First endpoint in normalized image coordinates [0,1] x [0,1] */
  p1: [number, number];
  /** Second endpoint in normalized image coordinates [0,1] x [0,1] */
  p2: [number, number];
  /** Known world-space distance between the two points */
  length: number;
}

/** A single vanishing line drawn on a reference image for camera alignment. */
export interface VanishingLine {
  /** Unique identifier */
  id: string;
  /** Which vanishing axis this line targets */
  axis: "x" | "y" | "z";
  /** Start point in normalized image coordinates [0,1] x [0,1] */
  start: [number, number];
  /** End point in normalized image coordinates [0,1] x [0,1] */
  end: [number, number];
}

// ---------------------------------------------------------------------------
// Undoable state snapshot — the subset of state we track for undo/redo
// ---------------------------------------------------------------------------

export interface UndoableSnapshot {
  /** Per-entry group transforms: entryId → GroupTransform clone */
  groupTransforms: Map<string, GroupTransform>;
  /** Blend file entries snapshot (immutable refs — not deep-cloned) */
  blendFiles: ReadonlyMap<string, BlendFileEntry>;
  /** Shape key weights (deep clone) */
  shapeKeyWeights: Map<string, Map<string, number>>;
  /** Reference image layers (shallow clone per layer, offset tuple spread) */
  referenceLayers: ReferenceLayer[];
  /** Active reference layer id */
  activeReferenceLayerId: string | null;
  /** Modifier overrides (deep clone) */
  modifierOverrides: Map<string, ModifierOverride[]>;
  /**
   * Scene objects snapshot — full deep-cloned `SceneObject`s.
   *
   * Cloned via `cloneSceneObject(o, cache)` so geometry buffers that
   * haven't changed since the previous snapshot are reused (structural
   * sharing). The single canonical `SceneObject` shape replaces what
   * used to be a duplicated inline type with flattened topology.
   */
  sceneObjects: SceneObject[];
  /** Material data blocks (deep clone of properties) */
  materials: MaterialDataBlock[];
  /** Mannequin morph weights (deep clone) */
  mannequinMorphs: Map<string, Map<string, number>>;
  /** Mannequin bone poses: objectId → boneName → quaternion [x,y,z,w] */
  mannequinBonePoses: Map<
    string,
    Map<string, [number, number, number, number]>
  >;
  /** Per-object visibility overrides */
  visibility: Map<string, boolean>;
  /** Locked bones: objectId → Set of locked bone names */
  lockedBones: Map<string, Set<string>>;
  /** Pinned bones: objectId → boneName → pinned world position [x,y,z] */
  pinnedBones: Map<string, Map<string, [number, number, number]>>;
  /** Bone volume scales: objectId → boneName → scale (1.0 = no change) */
  boneVolumes: Map<string, Map<string, number>>;
  /** Object collections */
  collections: Array<{
    id: string;
    name: string;
    transform: GroupTransform;
    objectIds: string[];
    cutterRef?: CollectionCutterRef;
    generatorRef?: import("../modeling/generators/types.ts").CollectionGeneratorRef;
  }>;
  /** Authoritative object-mode selection owners (union of sceneObject / blendEntry / collection). */
  selectedObjectTargets: ObjectSelectionTarget[];
  /** Edit mode selection (if in edit mode when snapshot was taken) */
  editSelection?: {
    objectId: string;
    selectedVertices: Set<number>;
    selectedEdges?: Set<number>;
    selectedFaces: Set<number>;
    hiddenVertices?: Set<number>;
    hiddenFaces?: Set<number>;
    curveEditVertices?: Float32Array;
    granularity?: EditGranularity;
    xray?: boolean;
    showVertexGroupColors?: boolean;
    isCurve?: boolean;
  };
  /** 3D cursor position. Selection flag is transient and not snapshot. */
  cursor3D: { position: [number, number, number] };
  /**
   * Scene-level sketch state - projected and screenspace strokes plus their
   * layers. Deep-cloned so undo/redo restores stroke creation, deletion,
   * layer ops, and erasure faithfully.
   */
  sketch: import("../modeling/sketch.ts").SceneSketchState;
  /** Inking editable raster snapshot (only set when inking sub-mode has produced a buffer). */
  inkingRaster?: {
    buffer: Uint8ClampedArray;
    width: number;
    height: number;
    dirtyFromBake: boolean;
  };
}

export interface EditSelectionSnapshot {
  objectId: string;
  selectedVertices: Set<number>;
  selectedEdges: Set<number>;
  selectedFaces: Set<number>;
  hiddenVertices: Set<number>;
  hiddenFaces: Set<number>;
  curveEditVertices?: Float32Array;
  granularity: EditGranularity;
  xray: boolean;
  showVertexGroupColors: boolean;
  showFaceGroupColors?: boolean;
  showMaterialSlotColors?: boolean;
  isCurve?: boolean;
  lastActiveVertex: number | null;
  lastActiveEdge: number | null;
  lastActiveFace: number | null;
}

/**
 * Object-local command baseline used by edit-mode modal commands. It carries
 * the cloned object and edit selection needed for live preview/cancel/replay
 * without cloning unrelated scene state.
 */
export interface EditObjectSnapshot {
  type: "edit-object-snapshot";
  object: SceneObject;
  editSelection: EditSelectionSnapshot;
  selectedObjectTargets: ObjectSelectionTarget[];
  cursor3D: { position: [number, number, number] };
}

export type CommandSnapshot = UndoableSnapshot | EditObjectSnapshot;

// ---------------------------------------------------------------------------
// Sculpt delta — lightweight undo entry for sculpt strokes
// ---------------------------------------------------------------------------

/**
 * Sparse undo entry for a single sculpt stroke.  Stores only the before/after
 * values of affected vertices — typically 100–1000x smaller than a full
 * UndoableSnapshot for high-poly meshes.
 */
export interface SculptDelta {
  type: "sculpt-delta";
  objectId: string;
  /** Sorted vertex indices that were modified by this stroke. */
  affected: Uint32Array;
  /** Before-stroke values (packed: 3 floats per affected vertex). */
  beforeVertices: Float32Array;
  /** After-stroke values (packed: 3 floats per affected vertex). */
  afterVertices: Float32Array;
}

/**
 * Selection-only undo entry. Selection clicks and box-selects don't
 * mutate geometry, so capturing a full UndoableSnapshot for them was
 * pure waste — the active edit/sculpt object even bypasses the clone
 * cache on snapshot capture, so a single click while editing a dense
 * mesh used to deep-clone the entire mesh just to record selection
 * state. SelectionDelta stores both `before` and `after` so undo and
 * redo can be applied symmetrically without ever capturing a snapshot.
 *
 * Selection-only call sites that push these include: object-mode
 * click (clickObjectMode), object-mode box select (boxSelectObjects),
 * cross-mode clear (clearSelection), edit-mode click (handleEditClick),
 * edit-mode clear (clearVertexSelection), edit-mode box select
 * (boxSelectEdit), and brush/circle select modal commits. Mode transitions
 * (enter/exit edit mode) stay on the full-snapshot path because they entail
 * real state changes.
 */
export interface SelectionDeltaState {
  selectedObjectTargets: ObjectSelectionTarget[];
  cursor3DSelected: boolean;
  lastObjectModeSelection: Set<string> | null;
  /** Edit-mode selection at the time of capture, or null when in
   *  object mode. The structure mirrors the live edit-mode selection
   *  fields so apply can write straight back. */
  editSelection: {
    objectId: string;
    selectedVertices: Set<number>;
    selectedEdges: Set<number>;
    selectedFaces: Set<number>;
    lastActiveVertex: number | null;
    lastActiveEdge: number | null;
    lastActiveFace: number | null;
  } | null;
  lastEditModeSelection: {
    objectId: string;
    selectedVertices: Set<number>;
    selectedEdges: Set<number>;
    selectedFaces: Set<number>;
    granularity: EditGranularity;
  } | null;
  mode: InteractionMode;
}

export interface SelectionDelta {
  type: "selection-delta";
  before: SelectionDeltaState;
  after: SelectionDeltaState;
}

/**
 * Sparse vertex-position undo entry for edit-mode transforms. Stores only the
 * vertices affected by the command, plus optional base-vertex values when the
 * sculpt reference mesh needs to move with the visible mesh.
 */
export interface VertexPositionDelta {
  type: "vertex-position-delta";
  objectId: string;
  affected: Uint32Array;
  beforeVertices: Float32Array;
  afterVertices: Float32Array;
  beforeBaseVertices?: Float32Array;
  afterBaseVertices?: Float32Array;
  beforeEditSelection: EditSelectionSnapshot;
  afterEditSelection: EditSelectionSnapshot;
}

export interface ObjectGeometryHistoryState {
  object: SceneObject;
  editSelection: EditSelectionSnapshot;
  selectedObjectTargets: ObjectSelectionTarget[];
}

/**
 * Object-scoped geometry snapshot for topology-changing edit operations.
 * This replaces full-app snapshots for modal edit operators while preserving
 * exact before/after object geometry and edit selection.
 */
export interface ObjectGeometrySnapshotDelta {
  type: "object-geometry-snapshot";
  objectId: string;
  before: ObjectGeometryHistoryState;
  after: ObjectGeometryHistoryState;
}

/** Union type for undo entries — full snapshot, sculpt delta, or
 *  selection-only delta. Dispatch via the `isSculptDelta` /
 *  `isSelectionDelta` type guards in storeUndo.ts. */
export type UndoEntry =
  | UndoableSnapshot
  | SculptDelta
  | SelectionDelta
  | VertexPositionDelta
  | ObjectGeometrySnapshotDelta;

// ---------------------------------------------------------------------------
// Camera bookmark
// ---------------------------------------------------------------------------

export interface CameraBookmark {
  id: string;
  name: string;
  position: [number, number, number];
  target: [number, number, number];
  focalLength: number;
}

// ---------------------------------------------------------------------------
// Blend file entry — one per imported .blend file
// ---------------------------------------------------------------------------

/** Group-level transform for a blend file entry (applied as parent to all objects). */
export interface GroupTransform {
  position: [number, number, number];
  /** Rotation stored as quaternion [x, y, z, w] for correct composition. */
  rotation: [number, number, number, number];
  scale: [number, number, number];
}

export const IDENTITY_GROUP_TRANSFORM: Readonly<GroupTransform> = {
  position: [0, 0, 0],
  rotation: [0, 0, 0, 1],
  scale: [1, 1, 1],
};

export interface BlendFileEntry {
  /** Stable ID, e.g. "bf_" + random suffix */
  readonly id: string;
  /** Parsed blend file data (objects, meshes, materials, etc.) */
  readonly file: BlendFile;
  /** Display name of the original file */
  readonly fileName: string;
  /** SHA-256 hex hash of the original buffer */
  readonly hash: string;
  /** Original file buffer for reset / re-export */
  readonly originalBuffer: ArrayBuffer;
  /** Group-level transform applied as parent to all objects in this entry. */
  readonly groupTransform: GroupTransform;
}

export type ObjectSelectionTarget =
  | { kind: "sceneObject"; id: string }
  | { kind: "blendEntry"; id: string }
  | { kind: "collection"; id: string };

// ---------------------------------------------------------------------------
// App state
// ---------------------------------------------------------------------------

export interface AppState {
  /** All loaded .blend files, keyed by entry ID */
  blendFiles: Map<string, BlendFileEntry>;
  /**
   * Authoritative object-mode selection owners. Each entry is a
   * `sceneObject`, `blendEntry`, or `collection` target. Leaf ids (for
   * rendering, outline passes, hit proxies) are derived on demand via
   * `selectionToLeafObjectIds` — never stored alongside, so there is one
   * source of truth for "what is selected."
   */
  selectedObjectTargets: ObjectSelectionTarget[];
  /** Object collections */
  collections: Map<string, Collection>;
  /** Per-object visibility overrides */
  visibility: Map<string, boolean>;
  /** Per-object render visibility (visible in render but still casts shadows when false) */
  renderVisibility: Map<string, boolean>;
  /** Per-object shading override — keyed by object name or scene object id */
  shadingOverrides: Map<string, ShadingMode>;
  /** Shape key weights: shapeKeySetName → keyName → weight */
  shapeKeyWeights: Map<string, Map<string, number>>;
  /** Camera focal length in mm */
  focalLength: number;
  /** Camera world-space position */
  cameraPosition: [number, number, number];
  /** Camera orbit target */
  cameraTarget: [number, number, number];
  /** Camera up vector (null = use default Z-up). */
  cameraUp: [number, number, number] | null;
  /**
   * Orthographic-camera half-height in world units — controls apparent scale
   * independent of the orbit distance (Blender-style ortho zoom). Seeded at
   * `dist × tan(fov/2)` when entering an axis view so apparent size matches
   * the perspective frame at entry; wheel / pinch scale this directly while
   * in ortho rather than dollying the camera along the view direction.
   */
  orthographicHalfHeight: number;
  /** Ordered reference image layers (bottom-to-top render order) */
  referenceLayers: ReferenceLayer[];
  /** Currently active reference layer id (null when no layers exist) */
  activeReferenceLayerId: string | null;
  /** Active vanishing line axis being drawn (null when not drawing) */
  alignCameraDrawAxis: "x" | "y" | "ref" | null;
  /** ID of the currently selected vanishing line (for editing/deletion) */
  selectedVanishingLineId: string | null;
  /**
   * Snapshot of the active layer's display state taken on entry to align
   * mode, restored on exit. Forces `above = true` and `opacity = 1.0`
   * during alignment so the reference image doesn't visually conflict
   * with the 3D scene.
   */
  alignModeSavedLayerState: {
    layerId: string;
    above: boolean;
    opacity: number;
  } | null;
  /** Current interaction mode */
  mode: InteractionMode;
  /** Whether camera orbiting is enabled (independent of interaction mode). */
  orbitEnabled: boolean;
  /** When true, primary single-pointer drags pan instead of orbiting. */
  singleDragPanEnabled: boolean;
  /** True while Space is held to temporarily enable orbit. */
  spaceOrbitActive: boolean;
  /** True while box select drag is active — suppresses camera orbit. */
  boxSelectActive: boolean;
  /** True while brush/circle select is active — suppresses camera orbit. */
  brushSelectActive: boolean;
  /**
   * Polymorphic single-slot state for the four Object Mode surface tools
   * (Box, Cylinder, Poly, Inset). Replaces the four parallel
   * `surfaceXStage` / `surfaceXDraft` / `surfaceXActive` triples that
   * existed before consolidation.
   *
   *  - `kind` discriminates which tool is armed; narrowing on `kind` exposes
   *    the matching `stage` and `draft` shapes.
   *  - `stage` is the modal substate; `null` means the tool is armed but
   *    has not yet picked a face.
   *  - `draft` is the in-progress geometry; `null` until the user starts
   *    drawing / drags the first corner.
   *  - `active` mirrors the historical `surfaceXActive` flags — true while
   *    a canvas drag owns the pointer (suppresses camera orbit, the same
   *    way `boxSelectActive` does for box-select). Cleared between stages
   *    so the user can orbit the camera before grabbing the gizmo arrow.
   *
   * `null` outside the slot means no surface tool is armed.
   */
  surfaceTool: SurfaceToolState | null;
  /** Active eyedropper pick session, or null when no pick is in progress. */
  eyedropperSession: EyedropperSession | null;
  /** The object id currently hovered in the viewport during an eyedropper session. */
  eyedropperHoveredId: string | null;
  /** Current transform gizmo mode */
  transformMode:
    | "translate"
    | "rotate"
    | "scale"
    | "refImage"
    | "pose"
    | "alignCamera";
  /** High-level workspace mode */
  workspaceMode: WorkspaceMode;
  /** Driver dependency graph */
  driverGraph: DriverGraph | null;
  /**
   * Per-scope render method + settings. Viewport scope applies in creation
   * modes (edit/object/pose/sketch/sculpt/reference); render scope applies in
   * output modes (inking/render). Use `selectActiveRenderConfig` /
   * `useActiveRenderConfig` from `renderConfig.ts` to read the right scope
   * for the current workspace. Never read a flat `viewMode`/`aoEnabled` off
   * the store — there's no global one; it lives inside one of these configs.
   *
   * Note: live-canvas DPR and shadow-map resolution do **not** live here —
   * see `viewportQuality` / `exportQualityOverrides` below.
   */
  renderConfigs: Record<
    import("./renderConfig.ts").RenderScope,
    import("./renderConfig.ts").RenderConfig
  >;
  /**
   * Global live-canvas quality (DPR + shadow-map resolution). Shared by
   * every workspace so draft ↔ render mode switches do not trigger a
   * canvas buffer reallocation. Edited from Settings → Viewport →
   * Quality. See `renderConfig.ts` for the type definition.
   */
  viewportQuality: import("./renderConfig.ts").ViewportQualitySettings;
  /**
   * Per-field overrides applied to offscreen export rendering (download,
   * inking PNG). `null` fields inherit `viewportQuality`. Resolve with
   * `resolveExportQuality` / `selectEffectiveExportQuality` from
   * `renderConfig.ts`.
   */
  exportQualityOverrides: import("./renderConfig.ts").ExportQualityOverrides;
  /** Show UV-mapped textures in solid mode when available */
  showTextures: boolean;
  /** Enable UV material maps (roughness, metallic, normal, emission) — global toggle */
  textureMapsEnabled: boolean;
  /** Whether the settings modal is open */
  settingsOpen: boolean;
  /** Tab to focus when the settings modal next opens, or null for the default. Consumed (and cleared) by SettingsModalInner on mount. */
  settingsInitialTab: string | null;
  /** Whether the render-method modal is open */
  renderMethodModalOpen: boolean;
  /** Transient flag — true while a generator parameter gesture is active, cleared shortly after release */
  generatorAdjusting: boolean;
  /** Object-mode X-ray — semi-transparent surfaces with no depth write */
  objectXray: boolean;
  /** Log10 of current camera distance to ground plane (for dynamic grid/snap) */
  gridLogDist: number;
  /** Show ground grid */
  showGrid: boolean;
  /** Show axis orientation indicator */
  showAxisIndicator: boolean;
  /** Show camera frustum frame overlay */
  showFrustum: boolean;
  /** Render output width in pixels. Authoritative for both export resolution
   *  and the camera frame overlay's aspect ratio. Clamped to
   *  [MIN_RENDER_DIM, MAX_RENDER_DIM] by its setters. */
  renderWidth: number;
  /** Render output height in pixels. Authoritative for both export resolution
   *  and the camera frame overlay's aspect ratio. Clamped to
   *  [MIN_RENDER_DIM, MAX_RENDER_DIM] by its setters. */
  renderHeight: number;
  /** Export with transparent background (alpha channel) */
  renderTransparentBackground: boolean;
  /** Inking outline effect (render mode only) */
  inkingEnabled: boolean;
  /** Maximum stroke half-width in pixels, applied at the camera near plane. */
  inkingThickness: number;
  /** Minimum stroke half-width in pixels, applied at the camera far plane.
   *  Distance between near and far plane is blended linearly. Set equal to
   *  inkingThickness to disable distance-based falloff. */
  inkingThicknessMin: number;
  /** Light-to-width response [0–1]. 0 = ignore scene-relative lighting;
   *  1 = strongly thicken dark-side linework and taper highlight-side linework. */
  inkingShadowResponse: number;
  /** Light bias [0–1] after visible-face normalization.
   *  0 = bias toward highlight linework, 0.5 = neutral, 1 = bias toward
   *  shadow linework. */
  inkingShadowExposure: number;
  /** Inking outline opacity [0–1] */
  inkingOpacity: number;
  /** Inking outline color (hex string) */
  inkingColor: string;
  /** Include scene primitives and props in the inking output. */
  inkingIncludePrimitives: boolean;
  /** Include mannequins in the inking output. */
  inkingIncludeMannequins: boolean;
  /** Include .blend file objects in the inking output. */
  inkingIncludeBlendObjects: boolean;
  /** Silhouette boost [0–1]. Higher values emphasize grazing-angle contours more strongly. */
  inkingLineWidthVariation: number;
  /** Crease detail intensity [0–1]. Higher values detect finer interior edges. */
  inkingDetail: number;
  /** Enable hatching shading overlay */
  inkingHatchingEnabled: boolean;
  /** Hatching line direction in degrees [0–180]. 0 = horizontal, 90 = vertical. */
  inkingHatchingDirection: number;
  /** Hatching light bias [0–2] after visible-face normalization.
   *  0 = favor highlight-side hatching, 0.5 = neutral, 1 = favor shadow-side hatching,
   *  values above 1 continue suppressing highlight-side hatching. */
  inkingHatchingExposure: number;
  /** Hatching line density / frequency [0–1.25]. */
  inkingHatchingDensity: number;
  /** Hatching spacing jitter [0–1]. Perturbs line spacing for an organic feel. */
  inkingHatchingJitter: number;
  /** World-unit distance at which the stroke is at full thicknessMax. */
  inkingDistanceNear: number;
  /** World-unit distance at which the stroke blends to thicknessMin. */
  inkingDistanceFar: number;
  /** When true, regenerate inking automatically after the camera settles. */
  inkingAutoGenerate: boolean;
  /** What to include in the rendered/exported image */
  renderExportMode: RenderExportMode;
  /**
   * Global override for per-material grid overlays in Render mode.
   * Defaults to "defer" (respects per-material settings).
   */
  renderGridOverride: RenderGridOverride;
  /** Camera projection mode */
  cameraProjection: CameraProjection;
  /**
   * Axis-aligned orthographic view lock. Non-null when the user is snapped to
   * one of the six cardinal views (front/back/left/right/top/bottom).
   * Cleared when an authoritative camera write steers the view off-axis, or
   * when the user explicitly dismisses it.
   */
  viewLock: ViewLock | null;
  /** Modal fly mode — WASD/QE move camera without modifiers while active */
  flyMode: boolean;
  /** UI scale factor (applied as CSS zoom to the entire page) */
  uiScale: number;
  /** Orbit (rotate) sensitivity multiplier — 1.0 is default */
  orbitSensitivity: number;
  /** Pan sensitivity multiplier — 1.0 is default */
  panSensitivity: number;
  /** Dolly (zoom) sensitivity multiplier — 1.0 is default */
  dollySensitivity: number;
  /** Invert orbit direction */
  invertOrbit: boolean;
  /** Invert pan direction */
  invertPan: boolean;
  /** Invert zoom (dolly) direction */
  invertZoom: boolean;
  /** Precision mode level — 0 = off, 0.5 = half, 0.25 = quarter input magnitudes */
  precisionLevel: number;
  /** Maximum retained bytes across undo history. Evicts oldest entries to stay under budget. */
  undoMemoryBudgetBytes: number;
  /** Global memory limit in bytes for the memory governor. 0 disables sampling, toasts, and the geometry gate. */
  memoryLimitBytes: number;
  /** Show debug overlay */
  debugEnabled: boolean;
  /** Debug material override — when non-null, all objects use this material config */
  debugMaterial: DebugMaterialConfig | null;
  /** Per-panel open/closed state for debug panels (persisted to localStorage) */
  debugPanelOpen: Record<DebugPanelId, boolean>;
  /** Active topology visualization mode (null = off) */
  activeTopologyViz: TopologyVizMode | null;
  /** Primitive-only symmetry debug axis for object-mode wireframe overlays. */
  primitiveSymmetryDebugAxis: PrimitiveSymmetryDebugAxis | null;
  /** Shape key layers disabled for debug inspection (only effective in pose mode + debug) */
  debugDisabledShapeKeyLayers: Set<ShapeKeyLayer>;
  /** Shading debug fix flags — each independently toggleable from the Shading Debug panel */
  shadingDebugFlags: ShadingDebugFlags;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Saved camera bookmarks */
  cameraBookmarks: CameraBookmark[];
  /** Whether undo/redo is available (reactive) */
  canUndo: boolean;
  canRedo: boolean;
  /** Favorite shape keys: "setRef::keyName" */
  favoriteShapeKeys: Set<string>;
  /** Favorite operator IDs (persisted, surfaced at top of operators panel) */
  favoriteOperators: Set<string>;
  /** Whether the floating operator search palette is open */
  operatorSearchOpen: boolean;
  /** Currently active toggle-mode operator (e.g. "extrude"), or null */
  activeOperator: string | null;
  /** Whether UI chrome (header, sidebar, toolbar, tool palette) is hidden (four-finger tap toggle) */
  chromeHidden: boolean;
  /** Whether the Add Object panel is open */
  addObjectOpen: boolean;
  /** When true, the Add Object panel should auto-focus its search field on open */
  addObjectFocusSearch: boolean;
  /** Whether the delete options menu is open (edit mode X/Delete) */
  deleteMenuOpen: boolean;
  /** Whether the render-method pie menu is open (Z in global context) */
  renderMethodPieOpen: boolean;
  /** Whether the cursor snap pie menu is open (Shift+S in object/edit/pose) */
  snapPieOpen: boolean;
  /** Whether the axis-view pie menu is open (` in global context) */
  viewPieOpen: boolean;
  /** Whether the extrude-variant pie menu is open (Alt+E in edit mode) */
  extrudePieOpen: boolean;
  /** Whether the merge options menu is open (edit mode M) */
  mergeMenuOpen: boolean;
  /** Whether the bone relax popover is open (pose mode X) */
  relaxMenuOpen: boolean;
  /** Whether the Simulate Gravity elasticity popover is open */
  gravityMenuOpen: boolean;
  /** Whether the Smooth Vertices popover is open (edit mode, launched
   *  from the mesh context menu). */
  smoothMenuOpen: boolean;
  /** Whether the object-mode delete confirmation popover is open */
  objectDeleteConfirmOpen: boolean;
  /** Whether the unified mesh context menu is open */
  meshMenuOpen: boolean;
  /** Which section of the mesh menu to show ("all" from header, or a specific section from hotkey) */
  meshMenuSection: "all" | "vertex" | "edge" | "face";
  /** Optional anchor point for operator UI spawned from the tool palette. */
  operatorAnchorPoint: { x: number; y: number } | null;
  /** Last-selected option ID per panel key, for Blender-style menu recall positioning. */
  panelLastSelected: Record<string, string>;
  /** Relax falloff: 0 = only selected bone, 1 = ±1 in hierarchy, 2 = ±2 in hierarchy */
  relaxFalloff: number;
  /** Runtime modifier overrides: objectName → override per modifier index */
  modifierOverrides: Map<string, ModifierOverride[]>;
  /** Per-object map of collapsed modifier indices. Transient UI state;
   *  not serialized. Lives in the store (rather than local component
   *  state) so that re-mounting the ModifiersPanel preserves collapse
   *  state and other panels can mirror the same collapse set. */
  modifierUICollapsed: Map<string, Set<number>>;

  // ---------------------------------------------------------------------------
  // Modeling subsystem
  // ---------------------------------------------------------------------------

  /** User-created scene objects keyed by id (primitives, imported OBJ, library spawns, mannequins) */
  sceneObjects: Map<string, SceneObject>;
  /** Global material registry — reusable material data blocks keyed by ID. */
  materials: Map<string, MaterialDataBlock>;
  /** Mannequin morph weights: objectId → { morphName → weight } */
  mannequinMorphs: Map<string, Map<string, number>>;
  /** Mannequin bone poses: objectId → boneName → quaternion [x,y,z,w] relative to rest pose */
  mannequinBonePoses: Map<
    string,
    Map<string, [number, number, number, number]>
  >;
  /** Locked bones: objectId → Set of locked bone names */
  lockedBones: Map<string, Set<string>>;
  /** Pinned bones: objectId → boneName → pinned world position [x,y,z].
   *  Pinned bones are held in place via whole-body IK when other bones are dragged. */
  pinnedBones: Map<string, Map<string, [number, number, number]>>;
  /** Proportion scales: objectId → regionId → scale (1.0 = no change) */
  boneVolumes: Map<string, Map<string, number>>;
  /** Per-mannequin constraint weight (0 = off, 1 = full). Missing → 1. */
  constraintWeights: Map<string, number>;
  /** Pose mode state, or null when not posing */
  poseMode: PoseModeState | null;
  /**
   * IK drag stabilization state — non-null during an active boneTranslate drag.
   * Exposes constraint-violation info so the visual layer can show error feedback.
   */
  ikDragState: {
    /** True when constraints are preventing the joint from following input. */
    isViolating: boolean;
    /** World position of the user's input (for glow placement). */
    violationWorldPos: [number, number, number];
  } | null;
  /** Edit mode state, or null when in object mode */
  editMode: EditModeState | null;
  /** Active transient geometry preview for the object currently being edited.
   *  This is the sole owner of live mesh preview during modal edit commands;
   *  committed `sceneObjects` stay unchanged until confirm.
   *
   *  Lifecycle invariant: this field references geometry buffers that are
   *  valid only while the referenced object's topology hasn't been rebuilt
   *  underneath. It MUST be cleared (to `null`) at every site that can
   *  invalidate that assumption. The full list today:
   *    - `confirmCommand`   (commandConfirm.ts) — preview promoted to object
   *    - `cancelCommand`    (commandLifecycle.ts) — preview discarded
   *    - `applySnapshot`    (storeUndo.ts) — undo/redo restores geometry
   *    - `exitEditMode`     (storeEditOps/selectionOps.ts)
   *    - `enterEditMode`    (storeEditOps/selectionOps.ts — fresh session)
   *    - `enterPoseMode`    (store.ts) — mode transition
   *    - `resetScene`       (store.ts) — blank-scene wipe
   *    - `loadFile`         (storeSceneFileOps.ts) — scene replaced
   *    - `addPrimitive`     (store.ts) — new object spawned
   *
   *  High-frequency mesh transform drags do NOT write here anymore; they
   *  live in the dedicated `liveEditPreview.ts` store so pointermove preview
   *  stays out of the global Zustand graph. This field remains the canonical
   *  store-owned preview for topology, curve, and other lower-frequency edit
   *  session patches that still need full app-state propagation.
   *
   *  `extractEditSessionPatch` (editSession.ts) is the single intended
   *  serializer for both this store-owned session and the live preview
   *  store. If a new code path mutates the edited object's `sceneObjects`
   *  entry without clearing the relevant preview store, the renderer will
   *  draw a stale preview over the freshly rebuilt committed geometry. */
  activeEditSession: ActiveEditSession | null;
  /** Id of the material slot currently highlighted in the sidebar panel
   *  (null when none or slot 0 is implied). Lives at app-state level — not
   *  inside `editMode` — because the slot list and picker are usable in both
   *  object and edit mode. Reset automatically when the selected object
   *  changes or the referenced slot no longer exists. Transient; not serialized. */
  activeMaterialSlotId: string | null;
  /** Persisted per-object edit session state (granularity, hidden geometry) that
   *  survives exit/re-enter cycles. Keyed by scene object ID. */
  editSessionCache: Map<
    string,
    {
      granularity: EditGranularity;
      hiddenVertices: Set<number>;
      hiddenFaces: Set<number>;
      xray?: boolean;
      showVertexGroupColors?: boolean;
    }
  >;
  /** Last object-mode selection — captured by clearSelection for recall. */
  lastObjectModeSelection: Set<string> | null;
  /** Last edit-mode selection — captured by clearSelection for recall.
   *  Scoped to a specific objectId; ignored if a different object is active. */
  lastEditModeSelection: {
    objectId: string;
    selectedVertices: Set<number>;
    selectedEdges: Set<number>;
    selectedFaces: Set<number>;
    granularity: EditGranularity;
  } | null;
  /** Sculpt mode state, or null when not sculpting */
  sculptMode: SculptModeState | null;
  /** Monotonic counter incremented when a sculpt stroke ends (pointerup).
   *  Subscribers use this to trigger deferred work (e.g. BVH rebuild). */
  sculptStrokeSeq: number;
  /** Sketch mode state, or null when not sketching */
  sketchMode: SketchModeState | null;
  /**
   * Scene-level sketch document - layers and strokes (projected + screenspace)
   * decoupled from any individual SceneObject. Authoritative source of truth
   * for everything the sketch overlays render.
   */
  sketch: import("../modeling/sketch.ts").SceneSketchState;
  /** Monotonic counter incremented when a sketch stroke ends (pointerup).
   *  Subscribers use this to trigger deferred work (e.g. material refresh). */
  sketchStrokeSeq: number;
  /** Inking mode state, or null when not in inking mode */
  inkingMode: InkingModeState | null;
  /** Shared drawing-input toggle for sculpt, sketch, and inking edit.
   *  When false, only pen input can draw; when true, mouse/touch can draw too.
   *  Transient UI state; not serialized. */
  allowNonPenDrawingInput: boolean;
  /** Saved inking views — each carries a raster snapshot plus the camera
   *  state at save time. Tapping one restores both. */
  savedInkingViews: SavedInkingView[];
  /** Pivot reference for transforms — applies in both object and edit mode */
  pivotReference: PivotReference;
  /** Transform orientation — defines which coordinate frame axis constraints use */
  transformOrientation: TransformOrientation;

  /** Proportional editing: when enabled, G/R/S affects nearby unselected vertices */
  proportionalEnabled: boolean;
  /** Proportional editing influence radius in screen-space CSS pixels */
  proportionalRadius: number;
  /** Proportional editing falloff curve */
  proportionalFalloff: ProportionalFalloff;

  /** Brush/circle select radius in screen-space CSS pixels */
  brushSelectRadius: number;

  /** Snapping: when enabled, translate commands snap to geometry targets */
  snapEnabled: boolean;
  /** Snap target type — what geometry element to snap to */
  snapTarget: SnapTarget;
  /** Align selected object rotation to snap targets that expose orientation. */
  snapWithRotation: boolean;
  /** In edit mode + surface snap, project each affected element individually. */
  surfaceProjectIndividual: boolean;

  // ---------------------------------------------------------------------------
  // 3D Cursor — Blender-style first-class scene helper
  // ---------------------------------------------------------------------------

  /** The 3D cursor's state (position, visibility, selection). */
  cursor3D: import("../modeling/cursor3d.ts").Cursor3DState;

  // ---------------------------------------------------------------------------
  // Command buffer (Blender-style numeric input)
  // ---------------------------------------------------------------------------

  /** Pre-selected axis constraint applied to the next command that starts. */
  pendingAxis: AxisConstraint;
  /** Active command buffer, or null when not in command input mode */
  commandBuffer: CommandBuffer | null;
  /** Snapshot used by applyCommandLive as source geometry for transforms */
  commandPreSnapshot: CommandSnapshot | null;
  /** Snapshot restored on cancel — when null, cancelCommand uses commandPreSnapshot */
  commandCancelSnapshot: CommandSnapshot | null;
  /** Last confirmed parameterized operation — drives the redo/adjust panel. */
  lastOperation: LastOperation | null;

  // ---------------------------------------------------------------------------
  // Object library
  // ---------------------------------------------------------------------------

  /** Saved object snapshots, persisted globally (not per blend file) */
  objectLibrary: LibraryEntry[];

  // ---------------------------------------------------------------------------
  // Scene file state
  // ---------------------------------------------------------------------------

  /** Name of the current .pr scene file, or null if never saved */
  currentSceneFileName: string | null;
  /** Whether the scene has unsaved changes since last .pr save */
  sceneDirty: boolean;
  sceneSaving: boolean;
  /** Original creation timestamp of the current scene (preserved across re-saves) */
  sceneCreatedAt: number | null;
  // Actions
  loadFile: (
    file: BlendFile,
    name: string,
    buffer?: ArrayBuffer,
    skipPersistence?: boolean,
  ) => Promise<void>;
  /** Remove a single blend file entry by ID */
  removeBlendFile: (id: string) => void;
  /** Reset scene to original blend file state, clearing all modifications */
  resetScene: () => void | Promise<void>;
  selectObject: (name: string | null, additive?: boolean) => void;
  /** Arm the viewport pick mode so the next scene-object tap calls onPick. */
  beginEyedropperSession: (
    onPick: (objectId: string) => void,
    onCancel: () => void,
    canPick?: (objectId: string) => boolean,
  ) => void;
  /** Disarm the active eyedropper session (called after pick or cancellation). */
  endEyedropperSession: () => void;
  /** Set the hovered object id during an active eyedropper session (null to clear). */
  setEyedropperHoveredId: (id: string | null) => void;
  toggleVisibility: (name: string) => void;
  /**
   * Batch visibility setter — flips every id to the same `visible` state
   * under a single undo entry. Used when a logical action hides a group
   * of related scene objects together (e.g. a generator hiding a wall
   * with its molding, baseboard, and attached doorways).
   */
  setObjectsVisibility: (ids: readonly string[], visible: boolean) => void;
  /** Hide selected objects (object mode) or selected geometry (edit mode). Blender H key. */
  hideSelection: () => void;
  hideUnselected: () => void;
  /** Unhide all hidden objects (object mode) or geometry (edit mode). Blender Alt+H. */
  unhideAll: () => void;
  setRenderVisible: (name: string, visible: boolean) => void;
  /** Set shading override for any object (blend or scene) */
  setShadingOverride: (name: string, shading: ShadingMode) => void;
  setShapeKeyWeight: (setName: string, keyName: string, weight: number) => void;
  setFocalLength: (mm: number) => void;
  setCameraOrbit: (
    position: [number, number, number],
    target: [number, number, number],
  ) => void;
  /** Set the ortho half-height (camera frustum vertical half-extent). Clamped. */
  setOrthographicHalfHeight: (halfHeight: number) => void;
  addReferenceLayer: (
    imageKey: string,
    imageUrl: string,
    name?: string,
  ) => void;
  deleteReferenceLayer: (layerId: string) => void;
  setActiveReferenceLayer: (layerId: string) => void;
  setRefLayerOffset: (layerId: string, offset: [number, number]) => void;
  setRefLayerScale: (layerId: string, scale: number) => void;
  setRefLayerRotation: (layerId: string, degrees: number) => void;
  setRefLayerOpacity: (layerId: string, opacity: number) => void;
  setRefLayerAbove: (layerId: string, above: boolean) => void;
  setRefLayerVisible: (layerId: string, visible: boolean) => void;
  setRefLayerName: (layerId: string, name: string) => void;
  setRefLayerImage: (
    layerId: string,
    imageKey: string,
    imageUrl: string,
  ) => void;
  refreshReferenceImageUrls: () => Promise<void>;
  moveReferenceLayer: (layerId: string, direction: "up" | "down") => void;
  setAlignCameraDrawAxis: (axis: "x" | "y" | "ref" | null) => void;
  setSelectedVanishingLine: (lineId: string | null) => void;
  addVanishingLine: (layerId: string, line: VanishingLine) => void;
  updateVanishingLineEndpoint: (
    layerId: string,
    lineId: string,
    which: "start" | "end",
    pos: [number, number],
  ) => void;
  deleteVanishingLine: (layerId: string, lineId: string) => void;
  clearVanishingLines: (layerId: string) => void;
  setGroundPlaneZ: (layerId: string, z: number) => void;
  setLiveSolve: (layerId: string, enabled: boolean) => void;
  setReferenceDistance: (
    layerId: string,
    ref: ReferenceDistance | undefined,
  ) => void;
  setMode: (mode: InteractionMode) => void;
  setOrbitEnabled: (
    enabled: boolean,
    options?: StoreActionFeedbackOptions,
  ) => void;
  setSingleDragPanEnabled: (
    enabled: boolean,
    options?: StoreActionFeedbackOptions,
  ) => void;
  setSpaceOrbitActive: (active: boolean) => void;
  setTransformMode: (mode: AppState["transformMode"]) => void;
  /** Write a single render-config field on a specific scope. */
  setRenderSetting: <K extends keyof import("./renderConfig.ts").RenderConfig>(
    scope: import("./renderConfig.ts").RenderScope,
    key: K,
    value: import("./renderConfig.ts").RenderConfig[K],
  ) => void;
  /** Write a single render-config field on the currently active scope. */
  setActiveRenderSetting: <
    K extends keyof import("./renderConfig.ts").RenderConfig,
  >(
    key: K,
    value: import("./renderConfig.ts").RenderConfig[K],
  ) => void;
  /** Write a single live viewport-quality field (DPR or shadow map). */
  setViewportQuality: <
    K extends keyof import("./renderConfig.ts").ViewportQualitySettings,
  >(
    key: K,
    value: import("./renderConfig.ts").ViewportQualitySettings[K],
  ) => void;
  /**
   * Write a single export-override field. Pass `null` to clear the
   * override and inherit the live viewport value.
   */
  setExportQualityOverride: <
    K extends keyof import("./renderConfig.ts").ExportQualityOverrides,
  >(
    key: K,
    value: import("./renderConfig.ts").ExportQualityOverrides[K],
  ) => void;
  /** Set high-level workspace mode — orchestrates mode/transformMode/editMode/poseMode. */
  setWorkspaceMode: (mode: WorkspaceMode) => void;
  /** Cycle render method within the active scope's available modes. */
  cycleRenderMethod: () => void;
  addLight: (kind: "point" | "directional") => void;
  /** Update light-specific properties on a scene object */
  updateSceneObjectLight: (id: string, patch: Partial<SceneLightProps>) => void;
  /**
   * Add an image plane sourced from a content-hashed reference image already
   * stored in `referenceImageDB` (the local IndexedDB store). Spawns at the
   * 3D cursor, oriented so its normal snaps to the world axis nearest the
   * camera's forward direction (Blender-style "view-aligned, axis-snapped").
   * The plane scales so its largest side spans 1 world unit and its aspect
   * matches `intrinsicWidth × intrinsicHeight`.
   */
  addImagePlane: (
    imageKey: string,
    intrinsicWidth: number,
    intrinsicHeight: number,
    name?: string,
  ) => void;
  /** Add a mannequin to the scene */
  addMannequin: (modelId: string) => void;
  /** Swap the mannequin body-type model */
  setMannequinModelId: (objectId: string, modelId: string) => void;
  /** Set a mannequin morph target weight */
  setMannequinMorph: (
    objectId: string,
    morphName: string,
    weight: number,
  ) => void;
  /** Enter pose mode for a mannequin */
  enterPoseMode: (objectId: string) => void;
  /** Exit pose mode */
  exitPoseMode: () => void;
  /** Select a bone within pose mode. Additive adds to selection (Shift+click). */
  selectBone: (
    boneName: string | null,
    objectId?: string,
    additive?: boolean,
  ) => void;
  /** Set a bone's pose quaternion */
  setBonePose: (
    objectId: string,
    boneName: string,
    quaternion: [number, number, number, number],
  ) => void;
  /** Set multiple bone poses at once (for IK solver results) */
  setBonePoses: (
    objectId: string,
    poses: Map<string, [number, number, number, number]>,
  ) => void;
  /** Reset all bone poses for a mannequin to rest pose */
  resetPose: (objectId: string) => void;
  /** Mirror a bone's pose (and its children) to the opposite side (L↔R) */
  mirrorBonePose: (objectId: string, boneName: string) => void;
  /** Drop an object or its bones onto the nearest surface below (or Z = 0).
   *
   *  Object mode: translates the object so its lowest mesh vertex (or lowest
   *  bone for mannequins) rests on the highest surface beneath it.
   *
   *  Pose mode – root bone: translates the entire model so its lowest joint
   *  rests on the nearest surface below.
   *  Pose mode – other bones: IK-solves each selected bone toward the
   *  nearest surface below within joint limits. */
  drop: (objectId: string) => void;
  /** Drop a whole collection onto the nearest surface as a single unit.
   *  Samples the bottom of every member, raycasts down, and shifts the
   *  collection's transform by the resulting delta so children retain
   *  their relative offsets within the group. Used by the Drop operator
   *  when the selection target is a `collection` (e.g. a live group
   *  generator like Room). */
  dropCollection: (collectionId: string) => void;
  /** Per-point gravity simulation for ropes, cables, and cloth. Drapes
   *  Bezier curves, skin-modifier wire chains, and faced meshes
   *  (bedsheets, tablecloths) over the scene under gravity, preserving
   *  edge lengths and colliding with all other meshes.
   *  Pinning rules:
   *    Curves – edit mode pins selected control points (defaulting to the
   *      first point if none are selected); object mode pins both endpoints
   *      of an open curve and nothing on a cyclic curve.
   *    Wire meshes – edit mode pins selected vertices; object mode pins
   *      every chain endpoint (valence-1 wire vertex). Vertices that
   *      participate in any face are always pinned.
   *    Cloth (faced mesh, no wire edges) – explicit pin group if given,
   *      else edit-mode selected vertices, else no pins (cloth drops
   *      freely). Bending and self-collision are cloth-only. */
  simulateGravity: (
    objectId: string,
    options?: {
      /** XPBD compliance (inverse stiffness) for structural edges. 0 =
       *  rigid (default), higher values stretch under load. See physicsSim.ts. */
      compliance?: number;
      /** XPBD compliance for bending constraints (cloth only). Lower =
       *  stiffer crease, higher = softer curl. Ignored for ropes and
       *  wire chains. Defaults to the solver's moderate built-in value. */
      bendingCompliance?: number;
      /** Rest-length multiplier. 1 = neutral (default), <1 pulls the
       *  chain taut between its pins. Ignored when nothing is pinned. */
      tautness?: number;
      /** Vertex group whose members become the user pin set, replacing
       *  the default endpoint / selection pinning rules. Mesh only —
       *  curves ignore this option (curve pinning is driven by
       *  edit-mode control-point selection instead). Missing / empty
       *  groups silently fall through to the defaults. */
      pinGroupId?: string;
      /** Particle–triangle self-collision thickness in world units (cloth
       *  only). 0 (default) disables self-collision. A positive value
       *  enforces that no free particle sits closer than `thickness` to
       *  any non-incident triangle, preventing cloth from passing through
       *  itself when it folds or stacks. */
      selfCollisionThickness?: number;
      /** Fraction of the solver's default substep budget to run, in the
       *  range `[0, 1]`. `1` (default) fully settles the drape; `0`
       *  runs no substeps and restores the baseline; intermediate
       *  values produce mid-drape timelines that the preview slider
       *  uses for time-scrubbing. Ignored when `maxSubsteps` is set. */
      substepFraction?: number;
      /** Hard upper bound on the solver's substep count. Overrides
       *  `substepFraction` when present. The menu's opt-in time-range
       *  bake uses this to define the bake's end-substep — pair with
       *  `recordStartSubstep` for the start. */
      maxSubsteps?: number;
      /** When true, skip the pushUndo call. Used by the live-preview
       *  slider so an entire drag session produces a single undo entry. */
      skipUndo?: boolean;
      /** When provided, simulate from this baseline geometry instead of
       *  reading the current SceneObject. Lets the live-preview slider
       *  re-run against the pre-simulation state on every frame so
       *  successive drags don't compound. */
      baseline?: {
        curveData?: CurveData;
        vertices?: Float32Array;
      };
      /** Per-particle snapshot stride for the bake recorder. The menu
       *  sets this to capture a scrubbable trajectory: every N substeps
       *  the solver snapshots particle positions; the menu interpolates
       *  between adjacent frames on Time-slider drag instead of re-
       *  running the solver. `0` (default) disables recording. Mesh /
       *  cloth paths only — curves skip recording and always run to a
       *  single final pose. */
      recordStride?: number;
      /** First substep at which the trajectory recorder begins capturing.
       *  Used by the menu's opt-in time-range bake — `0` (default) keeps
       *  the legacy semantics (initial pose recorded as frame 0). */
      recordStartSubstep?: number;
      /** Fires once the bake completes, delivering the trajectory in
       *  object-local space ready for Time-slider interpolation. Only
       *  set when `recordStride > 0`. Ignored by paths that don't
       *  support baking. */
      onBakeComplete?: (trajectory: GravityBakeResult) => void;
      /** Cancel the in-flight solve cooperatively at the next substep
       *  boundary. The promise rejects with a `GravitySimCancelledError`
       *  (caught internally) and no scene mutation happens. */
      signal?: AbortSignal;
      /** Streamed every few substeps from the worker — drives the
       *  ProgressBar in the operator panel. */
      onProgress?: (ev: PhysicsSimProgress) => void;
    },
  ) => Promise<void>;
  /**
   * Fast-path vertex commit for the bake scrub: writes `vertices` straight
   * to the SceneObject, recomputes `loopNormals`, skips undo. Used by the
   * Simulate Gravity menu while scrubbing the Time slider — the menu
   * interpolates between cached bake frames and calls this to see the
   * result without going through the full solver dispatch.
   */
  applyGravityFrame: (objectId: string, vertices: Float32Array) => void;
  /** Toggle lock state for a bone (locked bones are not affected by IK solving) */
  toggleBoneLock: (objectId: string, boneName: string) => void;
  /** Toggle pin state for a bone (pinned bones are held at their world position via whole-body IK) */
  toggleBonePin: (objectId: string, boneName: string) => void;
  /** Set per-mannequin constraint weight (0 = off, 1 = full) */
  setConstraintWeight: (objectId: string, weight: number) => void;
  /** Set a bone's volume scale (1.0 = default, >1 = inflate, <1 = deflate) */
  setBoneVolume: (objectId: string, boneName: string, scale: number) => void;
  setShowTextures: (show: boolean) => void;
  setTextureMapsEnabled: (enabled: boolean) => void;
  setSettingsOpen: (open: boolean) => void;
  /** Open the settings modal, optionally pre-focusing a tab by id. */
  openSettings: (initialTab?: string) => void;
  setRenderMethodModalOpen: (open: boolean) => void;
  setGeneratorAdjusting: (adjusting: boolean) => void;
  /** Set object-mode X-ray viewport shading */
  setObjectXray: (xray: boolean) => void;
  setGridLogDist: (logDist: number) => void;
  setShowGrid: (show: boolean) => void;
  setShowAxisIndicator: (show: boolean) => void;
  setShowFrustum: (show: boolean) => void;
  setRenderWidth: (px: number) => void;
  setRenderHeight: (px: number) => void;
  setRenderDimensions: (width: number, height: number) => void;
  setRenderTransparentBackground: (transparent: boolean) => void;
  setInkingEnabled: (enabled: boolean) => void;
  setInkingThickness: (thickness: number) => void;
  setInkingThicknessMin: (thickness: number) => void;
  setInkingShadowResponse: (response: number) => void;
  setInkingShadowExposure: (exposure: number) => void;
  setInkingOpacity: (opacity: number) => void;
  setInkingColor: (color: string) => void;
  setInkingIncludePrimitives: (included: boolean) => void;
  setInkingIncludeMannequins: (included: boolean) => void;
  setInkingIncludeBlendObjects: (included: boolean) => void;
  setInkingLineWidthVariation: (variation: number) => void;
  setInkingDetail: (detail: number) => void;
  setInkingHatchingEnabled: (enabled: boolean) => void;
  setInkingHatchingDirection: (direction: number) => void;
  setInkingHatchingExposure: (exposure: number) => void;
  setInkingHatchingDensity: (density: number) => void;
  setInkingHatchingJitter: (jitter: number) => void;
  setInkingDistanceNear: (distance: number) => void;
  setInkingDistanceFar: (distance: number) => void;
  setInkingAutoGenerate: (enabled: boolean) => void;
  /** Inking mode actions */
  setInkingFeatureEnabled: (
    feature: keyof InkingFeatureFlags,
    enabled: boolean,
  ) => void;
  setInkingCreaseAngle: (angle: number) => void;
  setInkingTaperStrength: (strength: number) => void;
  setInkingSimplificationTolerance: (tolerance: number) => void;
  setInkingCornerAngle: (angle: number) => void;

  /** Inking editable canvas actions */
  setInkingEditSubMode: (sub: InkingEditSubMode) => void;
  setInkingTool: (tool: InkingTool) => void;
  setInkingBrushSize: (size: number) => void;
  setInkingBrushOpacity: (opacity: number) => void;
  setInkingBrushHardness: (hardness: number) => void;
  setInkingBrushColor: (hex: string) => void;
  setInkingPressureMode: (mode: DrawingPressureMode) => void;
  setInkingShowReference: (show: boolean) => void;
  setInkingPan: (pan: [number, number]) => void;
  setInkingZoom: (zoom: number) => void;
  setInkingRotation: (rotationDeg: number) => void;
  /** Replace the raster buffer — called by the bake path after drawing. */
  setInkingRasterBuffer: (
    buffer: Uint8ClampedArray | null,
    width: number,
    height: number,
  ) => void;
  /** Bump rasterSeq and mark buffer as user-dirty; called after each stroke commits. */
  endInkingStroke: () => void;
  /** Reset pan/zoom to the fit-frame defaults. */
  resetInkingView: () => void;

  /** Save the current inking raster + camera as a reloadable view.
   *  Returns the new view's id, or null if nothing was saved (no bake yet). */
  saveCurrentInkingView: (name?: string) => string | null;
  /** Restore a saved view: camera animates to the stored position and the
   *  raster is replaced with the saved snapshot. */
  loadSavedInkingView: (id: string) => Promise<void>;
  /** Remove a saved view. Also removes its auto-created camera bookmark. */
  deleteSavedInkingView: (id: string) => void;
  /** Rename a saved view. */
  renameSavedInkingView: (id: string, name: string) => void;
  setRenderExportMode: (mode: RenderExportMode) => void;
  setRenderGridOverride: (mode: RenderGridOverride) => void;
  setCameraProjection: (proj: CameraProjection) => void;
  /**
   * Enter an axis-view lock and commit the snapped camera pose atomically.
   *
   * `distance` is supplied by the caller (live value read from OrbitControls
   * when available, falling back to the store) so the ortho half-height
   * seeded here matches the user's on-screen framing at the exact moment
   * of snapping.
   */
  enterViewLock: (
    axis: ViewAxis,
    sign: ViewSign,
    distance: number,
    position?: [number, number, number],
    target?: [number, number, number],
    up?: [number, number, number] | null,
  ) => void;
  /**
   * Exit the view lock. `reason` controls how the camera is restored:
   *  - `"manual"`: restore the snapshotted projection + focal length; the
   *    camera position is not touched (the user tapped the active view to
   *    cancel).
   *  - `"rotation"`: the user rotated past the break-out threshold.
   *    Projection / focal length are restored, but the new camera position
   *    is kept so the in-flight rotation gesture stays continuous.
   *  - `"projection"`: the user directly changed projection; clear the lock
   *    silently without restoring anything.
   */
  exitViewLock: (reason: "manual" | "rotation" | "projection") => void;
  updateGroupTransform: (entryId: string, transform: GroupTransform) => void;
  updateCollectionTransform: (
    collectionId: string,
    transform: GroupTransform,
  ) => void;
  setUiScale: (scale: number) => void;
  setOrbitSensitivity: (v: number) => void;
  setPanSensitivity: (v: number) => void;
  setDollySensitivity: (v: number) => void;
  setInvertOrbit: (v: boolean) => void;
  setInvertPan: (v: boolean) => void;
  setInvertZoom: (v: boolean) => void;
  setPrecisionLevel: (v: number) => void;
  setUndoMemoryBudget: (bytes: number) => void;
  setMemoryLimit: (bytes: number) => void;
  setDebugEnabled: (enabled: boolean) => void;
  setDebugMaterial: (config: DebugMaterialConfig | null) => void;
  /** Toggle a debug panel open/closed (persisted) */
  toggleDebugPanel: (id: DebugPanelId) => void;
  /** Set the active topology visualization mode (null to disable) */
  setActiveTopologyViz: (mode: TopologyVizMode | null) => void;
  /** Set the primitive symmetry debug axis (null to disable). */
  setPrimitiveSymmetryDebugAxis: (
    axis: PrimitiveSymmetryDebugAxis | null,
  ) => void;
  /** Toggle a shape key layer on/off for debug inspection */
  toggleDebugShapeKeyLayer: (layer: ShapeKeyLayer) => void;
  /** Toggle an individual shading debug fix flag */
  toggleShadingDebugFlag: (flag: keyof ShadingDebugFlags) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Push current state onto the undo stack (call before a mutation) */
  pushUndo: () => void;
  undo: () => void;
  redo: () => void;
  /** Camera bookmarks */
  addCameraBookmark: (
    name: string,
    position: [number, number, number],
    target: [number, number, number],
  ) => void;
  removeCameraBookmark: (id: string) => void;
  renameCameraBookmark: (id: string, name: string) => void;
  /** Create a new collection, optionally with initial object IDs */
  createCollection: (name: string, objectIds?: string[]) => string;
  /** Delete a collection (does not delete objects) */
  deleteCollection: (id: string) => void;
  /** Rename a collection */
  renameCollection: (id: string, name: string) => void;
  /**
   * Reset a collection's transform to identity and absorb the old transform
   * into each member's local TRS. Members remain visually fixed in world
   * space; only where the identity lives is redistributed.
   */
  applyCollectionTransform: (id: string) => void;
  /** Toggle visibility of a collection and all its objects */
  toggleCollectionVisibility: (id: string) => void;
  /** Add objects to a collection */
  addToCollection: (collectionId: string, objectIds: string[]) => void;
  /** Remove objects from a collection */
  removeFromCollection: (collectionId: string, objectIds: string[]) => void;
  /** Move objects to a collection (removes from all others first) */
  moveToCollection: (targetCollectionId: string, objectIds: string[]) => void;
  /** Select a collection owner. */
  selectCollection: (id: string | null) => void;
  /** Select an object-mode owner target directly. */
  selectObjectTarget: (
    target: ObjectSelectionTarget | null,
    additive?: boolean,
  ) => void;
  /** Box-select object origins by store key, promoting hits to their owner targets. */
  boxSelectObjects: (objectIds: Set<string>, additive: boolean) => void;
  /** Brush-select object origins by store key, promoting hits to their owner targets. */
  brushSelectObjects: (
    objectIds: Set<string>,
    mode: BrushSelectionMode,
  ) => void;
  /** Select a group of objects by name (e.g. all descendants of an Empty parent) */
  selectGroup: (objectNames: string[]) => void;
  /** Select all objects belonging to a blend file entry (monolithic selection) */
  selectBlendEntry: (entryId: string) => void;
  /** Toggle a shape key as favorite */
  toggleFavoriteShapeKey: (setRef: string, keyName: string) => void;
  /** Toggle an operator as favorite */
  toggleFavoriteOperator: (operatorId: string) => void;
  /** Open/close the floating operator search palette */
  setOperatorSearchOpen: (open: boolean) => void;
  /** Set or clear the active toggle-mode operator */
  setActiveOperator: (id: string | null) => void;
  /** Arm a viewport-driven tool operator from the tool palette without entering click-target mode. */
  armToolOperator: (id: string | null) => void;
  /** Toggle UI chrome visibility (four-finger tap) */
  toggleChromeHidden: () => void;
  /** Open/close the Add Object panel */
  setAddObjectOpen: (open: boolean) => void;
  /** Set whether the Add Object panel should auto-focus its search field on open */
  setAddObjectFocusSearch: (focus: boolean) => void;
  /** Open/close the delete options menu */
  setDeleteMenuOpen: (open: boolean) => void;
  /** Open/close the render-method pie menu */
  setRenderMethodPieOpen: (open: boolean) => void;
  /** Open/close the cursor snap pie menu */
  setSnapPieOpen: (open: boolean) => void;
  /** Open/close the axis-view pie menu */
  setViewPieOpen: (open: boolean) => void;
  /** Open/close the extrude-variant pie menu */
  setExtrudePieOpen: (open: boolean) => void;
  /** Open/close the merge options menu */
  setMergeMenuOpen: (open: boolean) => void;
  /** Open/close the bone relax popover */
  setRelaxMenuOpen: (open: boolean) => void;
  setGravityMenuOpen: (open: boolean) => void;
  /** Open/close the Smooth Vertices popover */
  setSmoothMenuOpen: (open: boolean) => void;
  /** Open/close the object-mode delete confirmation popover */
  setObjectDeleteConfirmOpen: (open: boolean) => void;
  /** Open/close the unified mesh context menu */
  setMeshMenuOpen: (open: boolean) => void;
  /** Set which section the mesh menu should display */
  setMeshMenuSection: (section: "all" | "vertex" | "edge" | "face") => void;
  /** Set anchor point for operator UI spawned from tool palette controls. */
  setOperatorAnchorPoint: (point: { x: number; y: number } | null) => void;
  /** Record the last-selected option for a panel (for menu recall positioning). */
  setPanelLastSelected: (panelKey: string, optionId: string) => void;
  /** Set the relax falloff depth (0 = selected only, 1 = ±1 neighbor, 2 = ±2 neighbors) */
  setRelaxFalloff: (falloff: number) => void;
  /** Relax selected bones from original poses toward rest pose by the given factor (0–1) */
  relaxBonesFrom: (
    originalPoses: Map<string, [number, number, number, number]>,
    factor: number,
  ) => void;
  /** Set modifier enabled state (runtime override) */
  setModifierEnabled: (
    objectName: string,
    modifierIndex: number,
    enabled: boolean,
  ) => void;
  /** Set a single modifier property override */
  setModifierProperty: (
    objectName: string,
    modifierIndex: number,
    property: string,
    value: unknown,
  ) => void;
  /** Atomically set the Array modifier's offset object by name, resolving and
   *  storing its TRS matrix at the same time (single undo step). */
  setArrayOffsetObject: (
    objectName: string,
    modifierIndex: number,
    targetName: string | null,
  ) => void;
  /** Add a runtime modifier to an object */
  addModifier: (objectName: string, kind: BlendModifier["kind"]) => void;
  /** Remove a user-added modifier */
  removeModifier: (objectName: string, modifierIndex: number) => void;
  /** Move a modifier up (-1) or down (+1) in the stack */
  moveModifier: (
    objectName: string,
    modifierIndex: number,
    direction: -1 | 1,
  ) => void;
  /** Toggle the collapsed UI state of a single modifier row. */
  toggleModifierCollapsed: (objectId: string, modifierIndex: number) => void;
  /** Drop all collapsed UI state for an object (e.g. on delete / undo). */
  clearModifierCollapsed: (objectId: string) => void;
  /** Focus camera on selected objects */
  focusSelection: () => void;

  // Modeling actions
  /** Add a primitive to the scene at the 3D cursor / origin */
  addPrimitive: (kind: PrimitiveKind) => void;
  /** Add an object generator instance at the 3D cursor. */
  addGenerator: (generatorId: string) => void;
  /** Live-update params, then schedule a latest-only background rebuild. */
  adjustGeneratorParams: (
    target: import("./storeGeneratorOps.ts").GeneratorTarget,
    nextParams: import("../modeling/generators/types.ts").GeneratorParams,
  ) => void;
  /** Commit a generator param gesture — gesture-end hook. */
  commitGeneratorEdit: (
    target: import("./storeGeneratorOps.ts").GeneratorTarget,
  ) => void;
  /** Remove generator ownership while preserving the current scene state. */
  applyGenerator: (
    target: import("./storeGeneratorOps.ts").GeneratorTarget,
  ) => void;
  /**
   * Rebuild every generator whose params reference the given source object.
   * Called from transform-update paths so generators that depend on other
   * objects (e.g. `cable` depending on its two endpoint empties) stay live
   * as the user drags their references around the viewport.
   */
  rebuildDependentGenerators: (sourceId: string) => void;
  /** Schedule rebuilds for a pre-resolved dependent target set —
   *  bypasses the per-source scan that `rebuildDependentGenerators`
   *  performs. Live drag commands cache the list at command-enter and
   *  reuse it across pointermove frames. */
  scheduleDependentRebuildTargets: (
    targets: ReadonlyArray<import("./storeGeneratorOps.ts").GeneratorTarget>,
  ) => void;
  /** Update curve data on a curve object and regenerate its bevel surface */
  updateCurveData: (id: string, patch: Partial<CurveData>) => void;
  /** Convert a curve to a regular mesh (strips curveData, keeps surface) */
  convertCurveToMesh: (id: string) => void;
  /** Remove a scene object by ID */
  removeSceneObject: (id: string) => void;
  /** Update a scene object's transform */
  updateSceneObjectTransform: (
    id: string,
    position: [number, number, number],
    rotation: [number, number, number],
    scale: [number, number, number],
  ) => void;
  /** Hide selected geometry in edit mode (H key). */
  hideEditSelection: () => void;
  hideEditUnselected: () => void;
  /** Unhide all geometry in edit mode (Alt+H). */
  unhideEditAll: () => void;
  /** Enter edit mode for a scene object */
  enterEditMode: (objectId: string) => void;
  /** Exit edit mode, returning to object mode */
  exitEditMode: () => void;
  /** Enter sculpt mode for a scene object */
  enterSculptMode: (objectId: string) => void;
  /** Exit sculpt mode, returning to object mode */
  exitSculptMode: () => void;
  /** Set the active sculpt brush type */
  setSculptBrush: (brush: SculptBrushType) => void;
  /** Set sculpt brush radius */
  setSculptRadius: (radius: number) => void;
  /** Set sculpt brush strength */
  setSculptStrength: (strength: number) => void;
  /** Set sculpt brush invert (add vs subtract) */
  setSculptInvert: (invert: boolean) => void;
  /** Initialise a grab sculpt stroke — locks affected vertices and weights */
  initGrabSculptStroke: (
    center: [number, number, number],
    objectLocalRadius?: number,
    viewDirectionLocal?: [number, number, number],
  ) => void;
  /** Apply a single sculpt stroke sample — called per pointermove */
  applySculptStroke: (params: {
    center: [number, number, number];
    pressure: number;
    normal: [number, number, number];
    grabTotalDelta?: [number, number, number];
    /** Object-local radius (converted from screen px by the handler). */
    radius?: number;
    /** Camera view direction in object-local space (for backface culling). */
    viewDirectionLocal?: [number, number, number];
    /** Overlap-attenuation multiplier — see computeOverlapFactor() in SculptHandler. */
    strengthMultiplier?: number;
  }) => void;
  /** Finalise a sculpt stroke — snapshot live buffers to store, bump sculptStrokeSeq */
  endSculptStroke: () => void;

  // -- Sketch mode actions (stroke-based drawing) --
  /** Enter sketch mode. No object required - projected points attach to
   *  whichever object the cursor hits, or float free in screenspace mode. */
  enterSketchMode: () => void;
  /** Exit sketch mode */
  exitSketchMode: () => void;
  /** Set active sketch tool */
  setSketchTool: (tool: SketchToolType) => void;
  /** Set stroke thickness in CSS pixels */
  setSketchThickness: (thickness: number) => void;
  /** Set stroke color */
  setSketchColor: (hex: string) => void;
  /** Switch between projected (surface-locked) and screenspace strokes. */
  setSketchProjection: (mode: SketchProjectionMode) => void;
  /** Toggle whether sculpt, sketch, and inking edit accept mouse/touch input, or remain pen-only */
  setAllowNonPenDrawingInput: (enabled: boolean) => void;
  /** Add a new stroke layer */
  addSketchLayer: () => void;
  /** Delete a stroke layer */
  deleteSketchLayer: (layerId: string) => void;
  /** Clear all strokes from a layer */
  clearSketchLayer: (layerId: string) => void;
  /** Set the active stroke layer */
  setActiveSketchLayer: (layerId: string) => void;
  /** Rename a stroke layer */
  renameSketchLayer: (layerId: string, name: string) => void;
  /** Set stroke layer opacity */
  setSketchLayerOpacity: (layerId: string, opacity: number) => void;
  /** Set stroke layer visibility */
  setSketchLayerVisible: (layerId: string, visible: boolean) => void;
  /** Begin a stroke layer color edit so drag updates share one undo snapshot */
  beginSketchLayerStrokeColorEdit: (layerId: string) => void;
  /** Set every stroke in a layer to one color */
  setSketchLayerStrokeColor: (layerId: string, color: string) => void;
  /** End a stroke layer color edit */
  endSketchLayerStrokeColorEdit: () => void;
  /** Reorder a stroke layer up or down in the stack. */
  moveSketchLayer: (layerId: string, direction: "up" | "down") => void;
  /**
   * Start a new stroke. The stroke's kind (projected vs screenspace) is
   * derived from whether the first point carries an attachment. For projected
   * strokes the caller supplies per-point host attachment, bind-pose hit, and
   * optionally baked skin weights plus a fallback plane locked at pointer-down.
   * For screenspace strokes only the world-space `p` is required.
   */
  beginStroke: (params: {
    p: [number, number, number];
    pressure: number;
    worldThickness: number;
    attachment?: {
      objectId: string;
      topologyVersion: number;
    };
    /** Locked at pointer-down for projected strokes; off-mesh samples are
     *  projected onto this plane. Absent for screenspace. */
    fallbackPlane?: {
      point: [number, number, number];
      normal: [number, number, number];
    };
    skinIndex?: [number, number, number, number];
    skinWeight?: [number, number, number, number];
    corners?: [number, number, number];
    bary?: [number, number, number];
  }) => void;
  /** Append a point to the in-flight stroke (no-op before beginStroke). */
  addStrokePoint: (params: SketchStrokePointInput) => void;
  /** Replace all points on the in-flight stroke. Used by ruler/line strokes. */
  replaceInFlightStrokePoints: (points: SketchStrokePointInput[]) => void;
  /** Delete every stroke whose id is in the given set, across all layers. */
  eraseStrokes: (ids: Set<string>) => void;
  /** Finalise a stroke — bump sketchStrokeSeq to publish the change. */
  endSketchStroke: () => void;

  /** Handle a click in edit mode — renderer reports the hit, store decides the selection */
  handleEditClick: (
    hit: import("./storeEditOps/index.ts").EditClickHit,
    modifiers: import("./storeEditOps/index.ts").EditClickModifiers,
  ) => void;
  /** Select/deselect vertices in edit mode */
  selectVertex: (index: number, additive?: boolean) => void;
  /** Select/deselect a group of vertices atomically */
  selectVertices: (indices: number[], additive?: boolean) => void;
  /** Clear all vertex selections */
  clearVertexSelection: () => void;
  /** Box-select: replace (or add to) the selection with the given vertex/edge/face sets. */
  boxSelectEdit: (
    vertices: Set<number>,
    edges: Set<number>,
    faces: Set<number>,
    additive: boolean,
  ) => void;
  /** Brush-select edit elements without pushing undo; the modal handler commits one delta. */
  brushSelectEdit: (
    vertices: Set<number>,
    edges: Set<number>,
    faces: Set<number>,
    mode: BrushSelectionMode,
  ) => void;
  /** Select the full connected component of the current edit selection. */
  /** Remove degenerate faces, duplicate faces, degenerate wire edges, and orphaned vertices */
  cleanUpMesh: () => {
    removedFaces: number;
    removedWireEdges: number;
    removedVertices: number;
  } | null;
  selectConnectedFromSelection: () => void;
  /**
   * Expand the current face selection to include every non-hidden face whose
   * unit normal is within `thresholdDegrees` of any selected face's normal.
   * Default threshold is 0° (exact-match within float tolerance). Captures a
   * `selection-seed` `lastOperation` so the redo panel can sweep the
   * threshold without compounding selections.
   */
  selectSimilarFacesByNormal: (thresholdDegrees?: number) => void;
  /** Laplacian-relax the current edit selection while re-projecting onto nearby visible surfaces. */
  relaxSelectionToSurface: () => void;
  /** Move selected vertices by a delta (in object-local space) */
  moveSelectedVertices: (delta: [number, number, number]) => void;
  /** Update vertex positions directly (for gizmo-driven transforms) */
  setVertexPositions: (objectId: string, vertices: Float32Array) => void;
  /** Update per-vertex skin radius scale factors directly */
  setSkinRadii: (objectId: string, skinRadii: Float32Array) => void;

  // --- Vertex groups -----------------------------------------------------
  /** Create a new empty vertex group on an object. Returns its id (null
   *  when the object is missing). Auto-activates the new group on the
   *  current edit session. */
  createVertexGroup: (objectId: string, name?: string) => string | null;
  /** Delete a vertex group by id. No-op when the group or object is missing. */
  deleteVertexGroup: (objectId: string, groupId: string) => void;
  /** Rename a vertex group. Empty / whitespace names are rejected. */
  renameVertexGroup: (objectId: string, groupId: string, name: string) => void;
  /** Set the currently active vertex group in the edit sidebar. `null` clears. */
  setActiveVertexGroup: (groupId: string | null) => void;
  /** Union the current selection into the given vertex group. */
  assignSelectionToGroup: (objectId: string, groupId: string) => void;
  /** Remove the current selection from the given vertex group (set-difference). */
  removeSelectionFromGroup: (objectId: string, groupId: string) => void;
  /** Replace / add / subtract a vertex group's indices into the active selection. */
  selectVertexGroup: (
    objectId: string,
    groupId: string,
    mode: "replace" | "add" | "subtract",
  ) => void;

  // --- Face groups (saved face selections) ------------------------------
  /** Create a new empty face group on an object. Returns its id (null
   *  when the object is missing). Auto-activates the new group on the
   *  current edit session. */
  createFaceGroup: (objectId: string, name?: string) => string | null;
  /** Delete a face group by id. No-op when the group or object is missing. */
  deleteFaceGroup: (objectId: string, groupId: string) => void;
  /** Rename a face group. Empty / whitespace names are rejected. */
  renameFaceGroup: (objectId: string, groupId: string, name: string) => void;
  /** Set the currently active face group in the edit sidebar. `null` clears. */
  setActiveFaceGroup: (groupId: string | null) => void;
  /** Union the current face selection into the given face group. */
  assignSelectionToFaceGroup: (objectId: string, groupId: string) => void;
  /** Remove the current face selection from the given face group. */
  removeSelectionFromFaceGroup: (objectId: string, groupId: string) => void;
  /** Replace / add / subtract a face group's faces into the active face selection. */
  selectFaceGroup: (
    objectId: string,
    groupId: string,
    mode: "replace" | "add" | "subtract",
  ) => void;

  // --- Per-face material assignment -------------------------------------
  /**
   * Assign every face in the current face selection to the given material
   * slot index. Writes `faceAttributes.materialIndex`. Captures one undo
   * snapshot. No-op when there is no face selection or the slot index is
   * out of range.
   */
  assignFacesToMaterialSlot: (objectId: string, slotIndex: number) => void;
  /**
   * Assign every face in a saved face group to the given material slot.
   * Bypasses `selectedFaces` so this works from object mode without needing
   * to enter edit mode first. Captures one undo snapshot.
   */
  assignFaceGroupToMaterialSlot: (
    objectId: string,
    slotIndex: number,
    faceGroupId: string,
  ) => void;
  /** Replace / add / subtract the faces using the given slot index into the active face selection. */
  selectFacesByMaterialSlot: (
    objectId: string,
    slotIndex: number,
    mode: "replace" | "add" | "subtract",
  ) => void;

  // --- Per-face shading override ----------------------------------------
  /**
   * Set the per-face shading override for every face in the current face
   * selection. `"inherit"` clears the override (face uses object-wide setting).
   * Captures one undo snapshot and triggers a loop-normal recompute.
   */
  setFaceShading: (
    objectId: string,
    mode: "inherit" | "flat" | "smooth" | "autoSmooth",
  ) => void;

  // --- Per-edge attributes (seam / sharp) -------------------------------
  /** Mark every resolved edge in the current selection as a UV seam. */
  markEdgeSeam: (objectId: string) => void;
  /** Clear the UV-seam flag from every resolved edge in the current selection. */
  clearEdgeSeam: (objectId: string) => void;
  /** Mark every resolved edge as sharp; triggers a loop-normal recompute. */
  markEdgeSharp: (objectId: string) => void;
  /** Clear the sharp flag from every resolved edge; triggers a loop-normal recompute. */
  clearEdgeSharp: (objectId: string) => void;
  /** Assign a crease weight in [0, 1] to every resolved edge. One call = one
   *  undo step — drive from a slider's commit-on-release, not per-tick. */
  setEdgeCrease: (objectId: string, value: number) => void;

  /** Create a new material data block with auto-generated name. Returns its ID. */
  createMaterial: (properties?: Partial<SceneMaterial>) => string;
  /** Delete a material data block. Slots referencing it are reassigned to a fallback. */
  deleteMaterial: (materialId: string) => void;
  /** Rename a material data block. */
  renameMaterial: (materialId: string, name: string) => void;
  /** Update properties on a material data block directly (all users see the change). */
  updateMaterial: (materialId: string, patch: Partial<SceneMaterial>) => void;
  /**
   * Toggle the "fake user" retention flag on a material. When enabled the
   * material is kept alive even with zero slot users; when disabled it becomes
   * eligible for automatic orphan cleanup.
   */
  setMaterialFakeUser: (materialId: string, fakeUser: boolean) => void;
  /** Point a specific material slot on an object at an existing material. */
  setMaterialSlotMaterial: (
    objectId: string,
    slotId: string,
    materialId: string,
  ) => void;
  /**
   * Break the shared-reference link on a specific slot by cloning its current
   * material into a new single-user block. No-op when the slot is already
   * single-user. Returns the resulting (possibly unchanged) material id.
   */
  makeUniqueMaterialForSlot: (objectId: string, slotId: string) => string;
  // --- Material slots ---
  /** Append a new material slot (creates a fresh MaterialDataBlock). */
  addMaterialSlot: (objectId: string) => void;
  /**
   * Remove a material slot. Refuses when the object has only one slot.
   * Per-face assignments are remapped: faces pointing at the removed slot
   * fall back to slot 0; faces pointing at later slots shift down by one.
   */
  removeMaterialSlot: (objectId: string, slotId: string) => void;
  /** Set the currently active material slot in the sidebar. `null` clears. */
  setActiveMaterialSlot: (slotId: string | null) => void;

  // --- Material library (cross-scene, cloud-backed via libraryStore) ---
  /**
   * Copy a scene material's properties into a new library item. Returns a
   * promise that resolves to the new library item id (or null on failure).
   */
  exportMaterialToLibrary: (materialId: string) => Promise<string | null>;
  /**
   * Create a new scene material from a library entry. The library material's
   * properties must be present in the cache — import fails silently otherwise.
   */
  importMaterialFromLibrary: (libraryId: string) => string;
  /** Set a scene object's shading mode */
  setSceneObjectShading: (id: string, shading: ShadingMode) => void;
  /** Set a scene object's auto-smooth angle and recompute normals */
  setSceneObjectAutoSmoothAngle: (id: string, angle: number) => void;
  /** Set pivot reference mode */
  setPivotReference: (ref: PivotReference) => void;
  /** Set transform orientation */
  setTransformOrientation: (o: TransformOrientation) => void;
  /** Set proportional editing enabled */
  setProportionalEnabled: (enabled: boolean) => void;
  /** Set proportional editing radius */
  setProportionalRadius: (radius: number) => void;
  /** Set proportional editing falloff curve */
  setProportionalFalloff: (falloff: ProportionalFalloff) => void;
  /** Set brush/circle select radius */
  setBrushSelectRadius: (radius: number) => void;
  /** Set snap enabled */
  setSnapEnabled: (enabled: boolean) => void;
  /** Set snap target type */
  setSnapTarget: (target: SnapTarget) => void;
  /** Toggle rotation alignment while snapping object transforms. */
  setSnapWithRotation: (enabled: boolean) => void;
  /** Toggle per-element projection while using surface snapping in edit mode. */
  setSurfaceProjectIndividual: (enabled: boolean) => void;

  // ---------------------------------------------------------------------------
  // 3D Cursor actions
  // ---------------------------------------------------------------------------

  /** Set the 3D cursor world position. */
  setCursor3DPosition: (position: [number, number, number]) => void;
  /**
   * Mark the cursor as selected/unselected. Selecting the cursor clears
   * object/edit/pose selection — mutual exclusivity is enforced in the
   * store's selection setters so cursor manipulation never collides with
   * other transform targets.
   */
  setCursor3DSelected: (selected: boolean) => void;
  /**
   * Move the cursor to a well-defined target derived from current state.
   * See `SnapCursorToKind` for the full list of targets (mirrors Blender's
   * Shift+S snap pie and the touch belt's "Snap Cursor To" menu).
   */
  snapCursorTo: (kind: import("./storeTypes.ts").SnapCursorToKind) => void;
  /**
   * Move the current selection onto the cursor. Acts over objects, vertices,
   * edges, faces, or origins depending on mode and `kind`.
   */
  snapSelectionToCursor: (
    kind: import("./storeTypes.ts").SnapSelectionToCursorKind,
  ) => void;
  /** Set edit mode selection granularity */
  setEditGranularity: (granularity: EditGranularity) => void;
  /** Toggle edit mode x-ray (see-through mesh, select occluded elements) */
  toggleEditXray: () => void;
  /** Toggle (or set) per-vertex-group surface coloring in edit mode. Display-only. */
  setShowVertexGroupColors: (value: boolean) => void;
  /** Toggle (or set) per-face-group surface coloring in edit mode. Display-only. */
  setShowFaceGroupColors: (value: boolean) => void;
  /** Toggle (or set) per-material-slot surface coloring in edit mode. Display-only. */
  setShowMaterialSlotColors: (value: boolean) => void;
  /** Delete selected vertices and all touching faces */
  deleteSelectedGeometry: () => void;
  /** Delete edges between selected vertices (remove touching faces, keep vertices) */
  deleteSelectedEdges: () => void;
  /** Delete only faces where all corners are selected (keep vertices) */
  deleteSelectedFaces: () => void;
  /** Delete selected edges and fully-selected faces, keep all vertices */
  deleteOnlyEdgesAndFaces: () => void;
  /** Delete only fully-selected faces, keep all edges and vertices */
  deleteOnlyFaces: () => void;
  /** Dissolve selected vertices, merging surrounding faces */
  dissolveSelectedVertices: () => void;
  /** Dissolve selected edges, merging adjacent face pairs */
  dissolveSelectedEdges: () => void;
  /** Dissolve selected faces into larger merged polygons */
  dissolveSelectedFaces: () => void;
  /** Subdivide the current mesh selection (enter modal subdivide command for meshes) */
  subdivideSelection: () => void;
  /** Enter modal edge loop insert. With edge: insert + slide. Without: pick-edge mode. */
  enterEdgeLoopInsert: (v0?: number, v1?: number) => void;
  /** Bridge two selected vertices with an edge */
  bridgeSelectedVertices: () => void;
  /** Blender-style fill: bridge 2 vertices or fill a closed boundary loop */
  fillSelection: () => void;
  /** Bridge two boundary edge loops with connecting quads (modal) */
  bridgeEdgeLoops: () => void;
  /** Fill a closed loop with a quad grid (modal) */
  gridFillSelection: () => void;
  /** Connect selected vertices by cutting through shared faces (Blender J key) */
  connectVertexPath: () => void;
  /** Extrude the current selection with modal distance adjustment */
  extrudeSelection: () => void;
  /** Extrude selection with per-vertex normal offsets (modal) */
  extrudeAlongNormalsSelection: () => void;
  /** Extrude each selected face independently along its own normal (modal) */
  extrudeIndividualFacesSelection: () => void;
  /** Manifold extrude: dissolves into coplanar neighbors, splits non-coplanar ones (modal) */
  extrudeManifoldSelection: () => void;
  /** Inset selected faces (enter modal inset command) */
  insetSelection: () => void;
  /** Bevel selected faces or edges (enter modal bevel command) */
  bevelSelection: () => void;
  /** Flip normals on selected geometry */
  flipNormals: () => void;
  /** Recalculate normals to point outward */
  recalculateOutside: () => void;
  /** Merge selected vertices using the given strategy */
  mergeSelection: (
    strategy: import("../modeling/meshOps.ts").MergeStrategy,
  ) => void;
  /** Enter modal merge-by-distance */
  mergeByDistanceSelection: () => void;
  /** Open the Smooth Vertices popover on the current selection. */
  smoothSelection: () => void;
  /** Execute uniform Laplacian smoothing from a captured baseline — called
   *  by SmoothMenu on each slider change. Reads the current editMode's
   *  selection, runs the WASM op on `baseline`, and patches the result
   *  into the active SceneObject. `axisMask` bits: 1=X, 2=Y, 4=Z. */
  smoothSelectionFrom: (
    baseline: Float32Array,
    factor: number,
    iterations: number,
    axisMask: number,
  ) => void;
  /** Set the handle type for selected curve control points */
  setCurveHandleType: (
    type: import("../modeling/types.ts").CurveHandleType,
  ) => void;
  /** Extend the curve from an endpoint */
  extendCurve: () => void;
  /** Toggle curve cyclic (open/closed) */
  toggleCurveCyclic: () => void;
  /** Delete selected curve control points */
  deleteCurvePoints: () => void;
  /** Apply transform: bake position/rotation/scale into vertices, reset to identity */
  applyTransform: (objectId: string) => void;
  /** Set origin to geometry center: shift vertices so their centroid is at local origin, adjust position to compensate */
  setOriginToGeometry: (objectId: string) => void;
  /** Set origin to world origin: shift vertices by current position, set position to 0 */
  setOriginToWorld: (objectId: string) => void;
  /** Set origin to an explicit world-space point, e.g. the 3D cursor. */
  setOriginToPosition: (
    objectId: string,
    worldPoint: [number, number, number],
  ) => void;
  /** Set origin to center of current edit-mode selection */
  setOriginToSelection: (objectId: string) => void;
  /**
   * Duplicate the current selection in place and enter translate mode.
   * In edit mode: duplicates selected vertices/edges/faces within the active object.
   * In object mode: duplicates selected scene objects (including mannequin state).
   * No-op when nothing is selected; owns pushUndo internally.
   */
  duplicate: () => void;
  /** Select all — objects (object mode) or geometry (edit mode) */
  selectAll: () => void;
  /** Invert the current object selection */
  invertSelection: () => void;
  /** Blender-style checker deselect: keep alternating elements in the active selection island */
  checkerDeselect: () => void;
  /** Clear the current selection */
  clearSelection: () => void;
  /** Restore the last selection in the current mode (captured by clearSelection) */
  recallSelection: () => void;
  /** Randomly select a percentage of visible objects/vertices */
  randomSelect: (coverage: number) => void;
  /** Separate selected faces into a new SceneObject */
  separateSelection: () => void;
  /** Rip selected vertices apart, then enter translate mode */
  ripSelection: (mouseX: number, mouseY: number) => void;
  /** Grow selection outward by one ring of adjacent elements */
  selectMore: () => void;
  /** Shrink selection inward by one ring of adjacent elements */
  selectLess: () => void;
  /** Join selected scene objects into one (Ctrl+J) */
  joinObjects: () => void;
  /** Join selected scene objects and weld coincident vertices (Ctrl+Alt+J) */
  mergeObjects: () => void;
  /** Rename a scene object's display name */
  renameSceneObject: (id: string, newName: string) => void;
  /** Apply a modifier: bake geometry through the modifier stack up to the given index, remove applied modifiers */
  applyModifier: (objectId: string, modifierIndex: number) => void;
  /** Delete every cutter object in a cutter collection and remove their live Boolean cuts */
  deleteCutterCollectionCuts: (collectionId: string) => void;
  /** Delete one cutter object from a cutter collection and remove its live Boolean cut */
  deleteCutterCollectionCut: (
    collectionId: string,
    cutterObjectId: string,
  ) => void;
  /** Bake a cutter collection into its host object, remove cutters, and collapse the collection */
  bakeCutterCollection: (collectionId: string) => Promise<void>;

  // Scene file actions
  /** Clear everything for a fresh scene */
  newScene: () => void;
  /** Save the current scene to a .pr file */
  saveSceneToFile: (forceNewFile?: boolean) => Promise<void>;
  /** Load a .pr scene file */
  loadSceneFromFile: (file: File) => Promise<void>;
  /** Add (merge) a .blend file from pre-parsed data into the current scene */
  addBlendFileParsed: (
    buffer: ArrayBuffer,
    hash: string,
    fileName: string,
  ) => Promise<void>;

  // Command buffer actions
  /** Enter command input mode (G/R/S pressed) */
  enterCommand: (
    type: CommandType,
    opts?: {
      axis?: AxisConstraint;
      normalVector?: [number, number, number];
      /** Bone-local axis for pose-mode rotation (preserves euler axis independence) */
      boneLocalAxis?: "x" | "y" | "z";
      preEditSelection?: Set<number>;
      preSnapshot?: CommandSnapshot;
      cancelSnapshot?: CommandSnapshot;
      gizmoInitiated?: boolean;
      /** Body-grab mode: auto-pin extremities and use whole-body IK. */
      bodyGrab?: boolean;
    },
  ) => void;
  /** Set axis constraint during command input */
  setCommandAxis: (axis: AxisConstraint) => void;
  /** Append a digit, '.', or '-' to the numeric input */
  appendCommandDigit: (char: string) => void;
  /** Remove last character from numeric input */
  backspaceCommand: () => void;
  /** Confirm and apply the command */
  confirmCommand: () => void;
  /** Cancel command and restore pre-command state */
  cancelCommand: () => void;
  /**
   * Set the polymorphic surface tool slot. Pass `null` to clear (disarm
   * the tool entirely). All four surface tools (Box, Cylinder, Poly,
   * Inset) write through this single setter; the discriminated `kind`
   * field decides the shape.
   */
  setSurfaceTool: (next: SurfaceToolState | null) => void;
  /**
   * Confirm the Surface Box modal — builds a new cube SceneObject from the
   * current draft, pushes undo, selects it, and clears the modal state.
   * Cancels (no scene mutation, no undo) when the footprint area or height
   * is below the confirm epsilon.
   */
  confirmSurfaceBox: () => void;
  /**
   * Confirm the Surface Cylinder modal — builds a generator-backed cylinder
   * SceneObject from the current draft and clears the modal state.
   */
  confirmSurfaceCylinder: () => void;
  /**
   * Confirm the Surface Poly modal — builds a new n-gon prism SceneObject
   * from the current draft, pushes undo, selects it, and clears the modal
   * state. Cancels (no scene mutation, no undo) when the polygon is
   * degenerate or the extrude height is below the confirm epsilon.
   */
  confirmSurfacePoly: () => void;
  /**
   * Confirm the Surface Inset modal — builds a generator-backed prism
   * SceneObject from the captured boundary loop + inset distance + height,
   * cuts the host or spawns a boss as appropriate, and clears the modal
   * state. Cancels (no scene mutation, no undo) when the inset distance is
   * non-positive or the extrude height is below the confirm epsilon.
   */
  confirmSurfaceInset: () => void;
  /** Apply a live mouse-driven transform preview */
  applyCommandLive: (
    screenX: number,
    screenY: number,
    opts?: { snapGridLevelOffset?: number },
  ) => void;
  /** Re-execute the last operation with new parameter values (redo panel). */
  adjustLastOperation: (
    params: Record<string, number | boolean | string>,
  ) => void;
  /** Clear the last operation record (invalidates redo panel). */
  clearLastOperation: () => void;

  // Library actions
  /** Snapshot a scene object into the global library */
  addToLibrary: (objectId: string) => void;
  /** Remove a library entry */
  removeFromLibrary: (entryId: string) => void;
  /** Spawn a copy of a library entry as a new scene object */
  spawnFromLibrary: (entryId: string) => void;
}
